import * as THREE from '../../libs/three.js/build/three.module.js';
import {
  ClipTask,
  ClipMethod,
  CameraMode,
  LengthUnits,
  ElevationGradientRepeat,
} from '../defines.js';
import { Renderer } from '../PotreeRenderer.js';
import { PotreeRenderer } from './PotreeRenderer.js';
import { EDLRenderer } from './EDLRenderer.js';
import { HQSplatRenderer } from './HQSplatRenderer.js';
import { Scene } from './Scene.js';
import { ClippingTool } from '../utils/ClippingTool.js';
import { TransformationTool } from '../utils/TransformationTool.js';
import { CloudTransformTool } from '../utils/CloudTransformTool.js';
import { Utils } from '../utils.js';
import { fitOrthographicDepthToBox } from '../utils/SharedUtils.js';
import { MapView } from './map.js';
import { ProfileWindow, ProfileWindowController } from './profile.js';
import { BoxVolume, Volume } from '../utils/Volume.js';
import { createRealityMeshClipController } from '../utils/RealityMeshClipping.js';
import { Features } from '../Features.js';
import { Message } from '../utils/Message.js';
import { Sidebar } from './sidebar.js';
import { PropertiesPanel } from './PropertyPanels/PropertiesPanel.js';

import { AnnotationTool } from '../utils/AnnotationTool.js';
import { MeasuringTool } from '../utils/MeasuringTool.js';
import { ProfileTool } from '../utils/ProfileTool.js';
import { VolumeTool } from '../utils/VolumeTool.js';
import { SurfaceTool } from '../utils/SurfaceTool.js';
import { SURFACE_GPU_CAP } from '../utils/Surface.js';
import { Measure } from '../utils/Measure.js';

import { InputHandler } from '../navigation/InputHandler.js';
import { NavigationCube } from './NavigationCube.js';
import { Compass } from '../utils/Compass.js';
import { OrbitControls } from '../navigation/OrbitControls.js';
import { FirstPersonControls } from '../navigation/FirstPersonControls.js';
import { EarthControls } from '../navigation/EarthControls.js';
import { DeviceOrientationControls } from '../navigation/DeviceOrientationControls.js';
import { VRControls } from '../navigation/VRControls.js';
import { EventDispatcher } from '../EventDispatcher.js';
import { ClassificationScheme } from '../materials/ClassificationScheme.js';
import { VRButton } from '../../libs/three.js/extra/VRButton.js';

import JSON5 from '../../libs/json5-2.1.3/json5.mjs';
import { Images360 } from '../modules/Images360/index.js';
import { MultiViewLayout } from './MultiView/MultiViewLayout.js';
import { MultiViewInputHandler } from './MultiView/MultiViewInputHandler.js';
import { markGuiLoaded } from './guiLoadTasks.js';

export class Viewer extends EventDispatcher {
  constructor(domElement, args = {}) {
    super();

    this.renderArea = domElement;
    this.guiLoaded = false;
    this.WMS = '';
    this.guiLoadTasks = [];
    // Bumped by cancelGuiLoad() so in-flight sidebar.html / profile.html
    // callbacks from a disposed viewer cannot append body-level UI (e.g. the
    // measurement snap flyout) after React has already left the route.
    this._guiLoadGeneration = 0;
    // Teardown callbacks for things the viewer attaches outside its own
    // render area (body-level modals, document listeners, timers) — see
    // onDispose(). $(renderArea).empty() cannot reach those.
    this._disposers = [];

    this.onVrListeners = [];
    this.sidebarVisible;
    this.messages = [];
    this.elMessages = $(`
		<div id="message_listing" style="position: absolute; z-index: 10005; left: 10px; bottom: 10px;">
		</div>`);
    $(domElement).append(this.elMessages);

    try {
      {
        // generate missing dom hierarchy
        if ($(domElement).find('#potree_map').length === 0) {
          let potreeMap = $(`
					<div id="potree_map" style="position: absolute; left: 50px; top: 50px; width: 400px; height: 400px; display: none; flex-direction: column;">
						<div id="potree_map_header" class="potree-dialog-titlebar">
							<span data-i18n="menu.map">Mapa</span>
							<button id="potree_map_close" class="potree-dialog-close" title="Zamknij">&#x2715;</button>
						</div>
						<div id="potree_map_content" class="map" style="flex: 1; min-height: 0;"></div>
					</div>
				`);
          $(domElement).append(potreeMap);
        }

        if ($(domElement).find('#potree_description').length === 0) {
          let potreeDescription = $(
            `<div id="potree_description" class="potree_info_text"></div>`
          );
          $(domElement).append(potreeDescription);
        }

        if ($(domElement).find('#potree_annotations').length === 0) {
          let potreeAnnotationContainer = $(`
					<div id="potree_annotation_container"
						style="position: absolute; z-index: 100000; width: 100%; height: 100%; pointer-events: none;"></div>`);
          $(domElement).append(potreeAnnotationContainer);
        }

        if ($(domElement).find('#potree_quick_buttons').length === 0) {
          let potreeMap = $(`
					<div id="potree_quick_buttons" class="quick_buttons_container" style="">
					</div>
				`);

          // {
          // 	let imgMenuToggle = document.createElement('img');
          // 	imgMenuToggle.src = new URL(Potree.resourcePath + '/icons/menu_button.svg').href;
          // 	imgMenuToggle.onclick = this.toggleSidebar;
          // 	// imgMenuToggle.classList.add('potree_menu_toggle');

          // 	potreeMap.append(imgMenuToggle);
          // }

          $(domElement).append(potreeMap);
        }
      }

      this.pointCloudLoadedCallback = args.onPointCloudLoaded || function () { };

      // if( /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ) {
      //	defaultSettings.navigation = "Orbit";
      // }

      this.server = null;

      this.fov = 60;
      this.isFlipYZ = false;
      this.useDEMCollisions = false;
      this.generateDEM = false;
      this.minNodeSize = 30;
      this.edlStrength = 1.0;
      this.edlRadius = 1.4;
      this.edlOpacity = 1.0;
      this.meshOpacity = 1.0;
      this.bimOpacity = 1.0;
      this.useEDL = true;
      this._edlRendererFailed = false;
      this.description = '';

      this.classifications = ClassificationScheme.DEFAULT;

      this.moveSpeed = 10;

      this.lengthUnit = LengthUnits.METER;
      this.lengthUnitDisplay = LengthUnits.METER;

      this.showBoundingBox = false;
      this.showAnnotations = true;
      this.freeze = false;
      this.clipTask = ClipTask.HIGHLIGHT;
      this.clipMethod = ClipMethod.INSIDE_ANY;

      this.elevationGradientRepeat = ElevationGradientRepeat.CLAMP;

      // Measure mode flag - when true, navigation is disabled, only measurement tools work
      this.measureModeEnabled = false;

      this.filterReturnNumberRange = [0, 7];
      this.filterNumberOfReturnsRange = [0, 7];
      this.filterGPSTimeRange = [-Infinity, Infinity];
      this.filterPointSourceIDRange = [0, 65535];

      this.potreeRenderer = null;
      this.edlRenderer = null;
      this.renderer = null;
      this.pRenderer = null;

      this.scene = null;
      this.sceneVR = null;
      this.overlay = null;
      this.overlayCamera = null;

      this.inputHandler = null;
      this.controls = null;

      this.clippingTool = null;
      this.transformationTool = null;
      /** @type {ReturnType<typeof createRealityMeshClipController> | null} */
      this.realityMeshClipController = null;
      /** Measurement / annotation snap targets (point cloud, reality mesh, BIM). */
      this.measurementSnapSources = {
        pointCloud: true,
        realityMesh: true,
        bim: true,
      };
      /** BIM geometry snap: 'point' (vertex) | 'line' (edge) | 'surface' (face). */
      this.measurementBimSnapMode = 'surface';
      this.navigationCube = null;
      this.compass = null;

      this.skybox = null;
      this.clock = new THREE.Clock();
      this.background = null;
      /** Host-provided site CRS (e.g. "EPSG:2177"); see setSiteProjection(). */
      this.siteProjection = null;
      this.editMode = false;

      this.initThree();

      if (args.noDragAndDrop) {
      } else {
        this.initDragAndDrop();
      }

      if (typeof Stats !== 'undefined') {
        this.stats = new Stats();
        this.stats.showPanel(0); // 0: fps, 1: ms, 2: mb, 3+: custom
        document.body.appendChild(this.stats.dom);
      }

      {
        let canvas = this.renderer.domElement;
        canvas.addEventListener(
          'webglcontextlost',
          (e) => {
            // dispose() forces a context loss on purpose.
            if (this._disposed) return;

            this.postMessage('WebGL context lost. \u2639');

            let gl = this.renderer.getContext();
            let error = gl.getError();
          },
          false
        );
      }

      {
        this.overlay = new THREE.Scene();
        this.overlayCamera = new THREE.OrthographicCamera(
          0,
          1,
          1,
          0,
          -1000,
          1000
        );
      }

      this.pRenderer = new Renderer(this.renderer);

      {
        let near = 2.5;
        let far = 10.0;
        let fov = 90;

        this.shadowTestCam = new THREE.PerspectiveCamera(90, 1, near, far);
        this.shadowTestCam.position.set(3.5, -2.8, 8.561);
        this.shadowTestCam.lookAt(new THREE.Vector3(0, 0, 4.87));
      }

      let scene = new Scene(this.renderer);

      {
        // create VR scene
        this.sceneVR = new THREE.Scene();

        // let texture = new THREE.TextureLoader().load(`${Potree.resourcePath}/images/vr_controller_help.jpg`);

        // let plane = new THREE.PlaneGeometry(1, 1, 1, 1);
        // let infoMaterial = new THREE.MeshBasicMaterial({map: texture});
        // let infoNode = new THREE.Mesh(plane, infoMaterial);
        // infoNode.position.set(-0.5, 1, 0);
        // infoNode.scale.set(0.4, 0.3, 1);
        // infoNode.lookAt(0, 1, 0)
        // this.sceneVR.add(infoNode);

        // window.infoNode = infoNode;
      }

      this.setScene(scene);

      {
        this.inputHandler = new InputHandler(this);
        this.inputHandler.setScene(this.scene);

        // Multi-view (single canvas, N panes): null when single-view is active.
        // Set by setMultiViewLayout(); torn down by clearMultiViewLayout().
        this.multiView = null;
        this.multiViewInputHandler = null;

        this.clippingTool = new ClippingTool(this);
        this.transformationTool = new TransformationTool(this);
        this.navigationCube = new NavigationCube(this);
        this.navigationCube.visible = false;

        this.compass = new Compass(this);

        this.createControls();

        this.clippingTool.setScene(this.scene);

        let onPointcloudAdded = (e) => {
          if (this.scene.pointclouds.length === 1) {
            let speed = e.pointcloud.boundingBox
              .getSize(new THREE.Vector3())
              .length();
            speed = speed / 5;
            this.setMoveSpeed(speed);
          }
        };

        let onVolumeRemoved = (e) => {
          this.inputHandler.deselect(e.volume);
        };

        this.addEventListener('scene_changed', (e) => {
          this.inputHandler.setScene(e.scene);
          this.clippingTool.setScene(this.scene);

          if (
            !e.scene.hasEventListener('pointcloud_added', onPointcloudAdded)
          ) {
            e.scene.addEventListener('pointcloud_added', onPointcloudAdded);
          }

          if (!e.scene.hasEventListener('volume_removed', onPointcloudAdded)) {
            e.scene.addEventListener('volume_removed', onVolumeRemoved);
          }
        });

        this.scene.addEventListener('volume_removed', onVolumeRemoved);
        this.scene.addEventListener('pointcloud_added', onPointcloudAdded);
      }

      {
        // set defaults
        this.setFOV(60);
        this.setEDLEnabled(true);
        this.setEDLRadius(1.4);
        this.setEDLStrength(0.4);
        this.setEDLOpacity(1.0);
        this.setMeshOpacity(1.0);
        this.setBimOpacity(1.0);
        this.setClipTask(ClipTask.HIGHLIGHT);
        this.setClipMethod(ClipMethod.INSIDE_ANY);
        this.setPointBudget(1 * 1000 * 1000);
        this.setShowBoundingBox(false);
        this.setFreeze(false);
        this.setControls(this.orbitControls);
        this.setBackground('gradient');
        this.scaleFactor = 1;
        this.setWMS('');

        this.loadSettingsFromURL();
      }

      // start rendering!
      //if(args.useDefaultRenderLoop === undefined || args.useDefaultRenderLoop === true){
      //requestAnimationFrame(this.loop.bind(this));
      //}

      this.renderer.setAnimationLoop(this.loop.bind(this));

      this.loadGUI = this.loadGUI.bind(this);

      this.annotationTool = new AnnotationTool(this);
      this.measuringTool = new MeasuringTool(this);
      this.profileTool = new ProfileTool(this);
      this.volumeTool = new VolumeTool(this);
      this.surfaceTool = new SurfaceTool(this);
      this.cloudTransformTool = new CloudTransformTool(this);
    } catch (e) {
      this.onCrash(e);
    }
    const timeTimer = Date.parse(
      localStorage.getItem('localTimerSavedProjectTime')
    );
    const savedTimer = Date.parse(
      localStorage.getItem('localSavedProjectTime')
    );
    let storedJsonString;
    if (false) {//timeTimer || savedTimer) {
      if (confirm('Project data detected in local storage. Load?')) {
        if (savedTimer > timeTimer || isNaN(timeTimer)) {
          storedJsonString = localStorage.getItem('localSavedProject');
        } else {
          storedJsonString = localStorage.getItem('localTimerSavedProject');
        }
        if (storedJsonString !== undefined) {
          const storedJsonData = JSON5.parse(storedJsonString);
          Potree.loadProject(this, storedJsonData);
          this.postMessage('Project loaded from local storage.', {
            duration: 1500,
          });
        }
      }
    }
  }

  onCrash(error) {
    $(this.renderArea).empty();

    if ($(this.renderArea).find('#potree_failpage').length === 0) {
      let elFailPage = $(`
			<div id="#potree_failpage" class="potree_failpage">

				<h1>Potree Encountered An Error </h1>

				<p>
				This may happen if your browser or graphics card is not supported.
				<br>
				We recommend to use
				<a href="https://www.google.com/chrome/browser" target="_blank" style="color:initial">Chrome</a>
				or
				<a href="https://www.mozilla.org/" target="_blank">Firefox</a>.
				</p>

				<p>
				Please also visit <a href="http://webglreport.com/" target="_blank">webglreport.com</a> and
				check whether your system supports WebGL.
				</p>
				<p>
				If you are already using one of the recommended browsers and WebGL is enabled,
				consider filing an issue report at <a href="https://github.com/potree/potree/issues" target="_blank">github</a>,<br>
				including your operating system, graphics card, browser and browser version, as well as the
				error message below.<br>
				Please do not report errors on unsupported browsers.
				</p>

				<pre id="potree_error_console" style="width: 100%; height: 100%"></pre>

			</div>`);

      let elErrorMessage = elFailPage.find('#potree_error_console');
      elErrorMessage.html(error.stack);

      $(this.renderArea).append(elFailPage);
    }

    throw error;
  }

  // ------------------------------------------------------------------------------------
  // Viewer API
  // ------------------------------------------------------------------------------------

  setWMS(WMS) {
    this.WMS += WMS;
  }

  setScene(scene) {
    if (scene === this.scene) {
      return;
    }

    let oldScene = this.scene;
    this.scene = scene;

    this.dispatchEvent({
      type: 'scene_changed',
      oldScene: oldScene,
      scene: scene,
    });

    {
      // Annotations
      $('.annotation').detach();

      // for(let annotation of this.scene.annotations){
      //	this.renderArea.appendChild(annotation.domElement[0]);
      // }

      this.scene.annotations.traverse((annotation) => {
        this.renderArea.appendChild(annotation.domElement[0]);
      });

      if (!this.onAnnotationAdded) {
        this.onAnnotationAdded = (e) => {
          e.annotation.traverse((node) => {
            this.renderArea.appendChild(node.domElement[0]);
            node.scene = this.scene;

            // Set up handles immediately so label is draggable right away
            if (node.viewer === null) {
              node.viewer = this;
              node.installHandles(this);
            }
          });
        };
      }

      if (oldScene) {
        oldScene.annotations.removeEventListener(
          'annotation_added',
          this.onAnnotationAdded
        );
      }
      this.scene.annotations.addEventListener(
        'annotation_added',
        this.onAnnotationAdded
      );
    }
  }

  setControls(controls) {
    if (controls !== this.controls) {
      if (this.controls) {
        this.controls.enabled = false;
        this.inputHandler.removeInputListener(this.controls);
      }

      this.controls = controls;
      this.controls.enabled = true;
      this.inputHandler.addInputListener(this.controls);
    }
  }

  getControls() {
    if (this.renderer.xr.isPresenting) {
      return this.vrControls;
    } else {
      return this.controls;
    }
  }

  getMinNodeSize() {
    return this.minNodeSize;
  }

  setMinNodeSize(value) {
    if (this.minNodeSize !== value) {
      this.minNodeSize = value;
      this.dispatchEvent({ type: 'minnodesize_changed', viewer: this });
    }
  }

  getBackground() {
    return this.background;
  }

  setBackground(bg) {
    if (this.background === bg) {
      return;
    }

    if (bg === 'skybox') {
      this.skybox = Utils.loadSkybox(
        new URL(Potree.resourcePath + '/textures/skybox2/').href
      );
    }

    // Swap gradient texture on the background mesh
    if (this.scene && this.scene.backgroundMesh) {
      if (bg === 'gradient-light') {
        this.scene.backgroundMesh.material.map = this.scene.backgroundTextureLight;
        this.scene.backgroundMesh.material.needsUpdate = true;
      } else if (bg === 'gradient' || bg === null) {
        this.scene.backgroundMesh.material.map = this.scene.backgroundTextureDark;
        this.scene.backgroundMesh.material.needsUpdate = true;
      }
    }

    this.background = bg;
    this.dispatchEvent({ type: 'background_changed', viewer: this });
  }

  setDescription(value) {
    this.description = value;

    $('#potree_description').html(value);
    //$('#potree_description').text(value);
  }

  getDescription() {
    return this.description;
  }

  setShowBoundingBox(value) {
    if (this.showBoundingBox !== value) {
      this.showBoundingBox = value;
      this.dispatchEvent({ type: 'show_boundingbox_changed', viewer: this });
    }
  }

  getShowBoundingBox() {
    return this.showBoundingBox;
  }

  setMoveSpeed(value) {
    if (this.moveSpeed !== value) {
      this.moveSpeed = value;
      this.dispatchEvent({
        type: 'move_speed_changed',
        viewer: this,
        speed: value,
      });
    }
  }

  getMoveSpeed() {
    return this.moveSpeed;
  }

  setWeightClassification(w) {
    for (let i = 0; i < this.scene.pointclouds.length; i++) {
      this.scene.pointclouds[i].material.weightClassification = w;
      this.dispatchEvent({
        type: 'attribute_weights_changed' + i,
        viewer: this,
      });
    }
  }

  setFreeze(value) {
    value = Boolean(value);
    if (this.freeze !== value) {
      this.freeze = value;
      this.dispatchEvent({ type: 'freeze_changed', viewer: this });
    }
  }

  getFreeze() {
    return this.freeze;
  }

  getClipTask() {
    return this.clipTask;
  }

  getClipMethod() {
    return this.clipMethod;
  }

  /**
   * Ask the reality-mesh clip controller to re-scan materials.
   * Host apps (e.g. noach-fe TilesRenderer) should call this when new
   * tile models load so LOD materials pick up clip hooks promptly.
   */
  requestRealityMeshClipMaterialScan() {
    if (!this.realityMeshClipController) {
      this.realityMeshClipController = createRealityMeshClipController();
    }
    this.realityMeshClipController.requestMaterialScan();
  }

  setClipTask(value) {
    if (this.clipTask !== value) {
      this.clipTask = value;

      this.dispatchEvent({
        type: 'cliptask_changed',
        viewer: this,
      });
    }
  }

  setClipMethod(value) {
    if (this.clipMethod !== value) {
      this.clipMethod = value;

      this.dispatchEvent({
        type: 'clipmethod_changed',
        viewer: this,
      });
    }
  }

  setElevationGradientRepeat(value) {
    if (this.elevationGradientRepeat !== value) {
      this.elevationGradientRepeat = value;

      this.dispatchEvent({
        type: 'elevation_gradient_repeat_changed',
        viewer: this,
      });
    }
  }

  setPointBudget(value) {
    const intValue = parseInt(value);
    if (Potree.pointBudget !== intValue) {
      Potree.pointBudget = intValue;
      this.dispatchEvent({ type: 'point_budget_changed', viewer: this });
    }

  }

  getPointBudget() {
    return Potree.pointBudget;
  }

  // Self-regulating backoff: if the renderer had to clamp the visible-nodes texture
  // (more visible nodes than the texture can hold), lower the point budget so the scene
  // settles at a level this device/scene can actually render. Debounced + persisted.
  _applyVisibleNodesFailsafe() {
    if (!this.pRenderer || !this.pRenderer._visibleNodesOverflow) {
      return;
    }
    this.pRenderer._visibleNodesOverflow = false;

    const now = performance.now();
    if (now - (this._lastBudgetDrop || 0) < 2000) {
      return; // cooldown 2 s to avoid collapsing the budget over consecutive frames
    }
    this._lastBudgetDrop = now;

    const current = this.getPointBudget();
    const reduced = Math.max(100000, Math.round(current * 0.7)); // -30%, floor 100k
    if (reduced < current) {
      this.setPointBudget(reduced);
      try { localStorage.setItem('potree_mobile_point_budget', String(reduced)); } catch (e) { /* noop */ }
      // Throttle the user-facing toast to at most once per 10 s so a sustained backoff
      // (budget still steps down every 2 s) doesn't stack a wall of messages.
      if (now - (this._lastBudgetToast || 0) >= 10000) {
        this._lastBudgetToast = now;
        try { this.postMessage(`Obniżono budżet punktów do ${reduced.toLocaleString()} (ochrona przed przeciążeniem).`); } catch (e) { /* noop */ }
      }
    }
  }

  isMobileDevice() {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
      (typeof window.orientation !== 'undefined') ||
      (window.matchMedia && window.matchMedia('(max-width: 768px)').matches) ||
      ('ontouchstart' in window && navigator.maxTouchPoints > 1);
  }

  /**
   * Oblicza optymalny budżet punktów dla urządzenia mobilnego
   * na podstawie dostępnej pamięci RAM i możliwości GPU.
   * Wartość jest pomniejszona o 20% jako margines bezpieczeństwa.
   */
  getOptimalMobilePointBudget() {
    let baseBudget = 500000; // domyślna wartość

    // Wykryj iOS/iPadOS (Safari i wszystkie przeglądarki na iOS używają WebKit)
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) ||
      (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1); // iPadOS 13+

    if (isIOS) {
      // iOS/iPadOS - navigator.deviceMemory nie działa, użyj heurystyki
      const screenHeight = Math.max(window.screen.height, window.screen.width) * (window.devicePixelRatio || 1);
      const cpuCores = navigator.hardwareConcurrency || 4;


      // iPad Pro (M1/M2/M4) - bardzo wysokie rozdzielczości i 8+ rdzeni
      if (screenHeight >= 2732 && cpuCores >= 8) {
        baseBudget = 3000000; // 3M dla iPad Pro z chipem M
      }
      // iPad Pro / iPhone Pro Max - wysokie rozdzielczości
      else if (screenHeight >= 2500) {
        baseBudget = 2000000; // 2M dla Pro Max / iPad Pro
      }
      // iPhone Pro / iPad Air / standardowe iPady
      else if (screenHeight >= 2000) {
        baseBudget = 1500000; // 1.5M dla Pro / iPad Air
      }
      // iPhone standardowy / iPad mini
      else if (screenHeight >= 1500) {
        baseBudget = 1000000; // 1M
      }
      // Starsze urządzenia
      else {
        baseBudget = 500000; // 500K
      }
    } else if (navigator.deviceMemory) {
      // Android/Chrome - użyj deviceMemory (zwraca wartości: 0.25, 0.5, 1, 2, 4, 8 GB)
      const memory = navigator.deviceMemory;

      if (memory <= 2) baseBudget = 100000;       // 100K dla słabych urządzeń
      else if (memory <= 4) baseBudget = 500000;  // 500K dla średnich
      else if (memory <= 6) baseBudget = 1000000; // 1M dla dobrych
      else baseBudget = 2000000;                   // 2M dla flag shipów (8GB+)
    } else {
      // Fallback na WebGL dla innych urządzeń
      try {
        const canvas = document.createElement('canvas');
        const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
        if (gl) {
          const maxTextureSize = gl.getParameter(gl.MAX_TEXTURE_SIZE);

          if (maxTextureSize <= 4096) baseBudget = 100000;       // 100K
          else if (maxTextureSize <= 8192) baseBudget = 500000;  // 500K
          else baseBudget = 1000000;                              // 1M
        }
      } catch (e) {
        console.warn('[Mobile] Could not detect WebGL capabilities');
      }
    }

    // Margines bezpieczeństwa 0.8x (20% mniej)
    const finalBudget = Math.round(baseBudget * 0.8);
    return finalBudget;
  }

  setShowAnnotations(value) {
    if (this.showAnnotations !== value) {
      this.showAnnotations = value;
      this.dispatchEvent({ type: 'show_annotations_changed', viewer: this });
    }
  }

  getShowAnnotations() {
    return this.showAnnotations;
  }

  setDEMCollisionsEnabled(value) {
    if (this.useDEMCollisions !== value) {
      this.useDEMCollisions = value;
      this.dispatchEvent({ type: 'use_demcollisions_changed', viewer: this });
    }
  }

  getDEMCollisionsEnabled() {
    return this.useDEMCollisions;
  }

  isEDLSupported() {
    try {
      return Features?.SHADER_EDL?.isSupported?.() !== false;
    } catch {
      return true;
    }
  }

  setEDLEnabled(value) {
    const wanted = Boolean(value);
    if (wanted) this._edlRendererFailed = false;
    value = wanted && this.isEDLSupported();

    if (this.useEDL !== value) {
      this.useEDL = value;
      this.dispatchEvent({ type: 'use_edl_changed', viewer: this });
    }
  }

  checkEditRights() {
    if (this.canEditPanoramas !== undefined) {
      this.dispatchEvent({
        type: 'edit_rights_changed',
        canEdit: !!this.canEditPanoramas,
      });
      return;
    }

    // Legacy path — standalone Potree with DOM checkbox
    const canEditEl = document.getElementById('can-edit');
    if (canEditEl) {
      this.canEditPanoramas = canEditEl.checked;
      this.dispatchEvent({
        type: 'edit_rights_changed',
        canEdit: !!this.canEditPanoramas,
      });
    }
  }

  getEDLEnabled() {
    return this.useEDL;
  }

  /**
   * EDL is a cheap post-process on WebGL2, so a throw here is almost always a
   * driver/shader glitch rather than "GPU too old". Drop back to the regular
   * renderer instead of showing the fatal Potree crash page.
   * @returns {boolean} true if EDL was on and is now disabled (caller may retry)
   */
  disableEDLAfterFailure(error) {
    if (!this.useEDL || this._edlRendererFailed) return false;
    this._edlRendererFailed = true;
    this.edlRenderer = null;
    this.setEDLEnabled(false);
    console.warn('EDL disabled after renderer failure', error);
    try {
      this.postMessage('EDL disabled (renderer error).');
    } catch {
      /* overlay may not exist yet */
    }
    return true;
  }

  setEDLRadius(value) {
    if (this.edlRadius !== value) {
      this.edlRadius = value;
      this.dispatchEvent({ type: 'edl_radius_changed', viewer: this });
    }
  }

  getEDLRadius() {
    return this.edlRadius;
  }

  setEDLStrength(value) {
    if (this.edlStrength !== value) {
      this.edlStrength = value;
      this.dispatchEvent({ type: 'edl_strength_changed', viewer: this });
    }
  }

  getEDLStrength() {
    return this.edlStrength;
  }

  setEDLOpacity(value) {
    if (this.edlOpacity !== value) {
      this.edlOpacity = value;
      this.dispatchEvent({ type: 'edl_opacity_changed', viewer: this });
    }
  }

  getEDLOpacity() {
    return this.edlOpacity;
  }

  setMeshOpacity(value) {
    if (this.meshOpacity !== value) {
      this.meshOpacity = value;
      this.dispatchEvent({ type: 'mesh_opacity_changed', viewer: this });
    }
  }

  getMeshOpacity() {
    return this.meshOpacity;
  }

  setBimOpacity(value) {
    if (this.bimOpacity !== value) {
      this.bimOpacity = value;
      this.dispatchEvent({ type: 'bim_opacity_changed', viewer: this });
    }
  }

  getBimOpacity() {
    return this.bimOpacity;
  }

  setFOV(value) {
    if (this.fov !== value) {
      this.fov = value;
      this.dispatchEvent({ type: 'fov_changed', viewer: this });
    }
  }

  getFOV() {
    return this.fov;
  }

  disableAnnotations() {
    this.scene.annotations.traverse((annotation) => {
      annotation.domElement.css('pointer-events', 'none');

      // return annotation.visible;
    });
  }

  enableAnnotations() {
    this.scene.annotations.traverse((annotation) => {
      annotation.domElement.css('pointer-events', 'auto');

      // return annotation.visible;
    });
  }

  setClassifications(classifications) {
    this.classifications = classifications;

    this.dispatchEvent({ type: 'classifications_changed', viewer: this });
  }

  setClassificationVisibility(key, value) {
    if (!this.classifications[key]) {
      this.classifications[key] = { visible: value, name: 'no name' };
      this.dispatchEvent({
        type: 'classification_visibility_changed',
        viewer: this,
      });
    } else if (this.classifications[key].visible !== value) {
      this.classifications[key].visible = value;
      this.dispatchEvent({
        type: 'classification_visibility_changed',
        viewer: this,
      });
    }
  }

  toggleAllClassificationsVisibility() {
    let numVisible = 0;
    let numItems = 0;
    for (const key of Object.keys(this.classifications)) {
      if (this.classifications[key].visible) {
        numVisible++;
      }
      numItems++;
    }

    let visible = true;
    if (numVisible === numItems) {
      visible = false;
    }

    let somethingChanged = false;

    for (const key of Object.keys(this.classifications)) {
      if (this.classifications[key].visible !== visible) {
        this.classifications[key].visible = visible;
        somethingChanged = true;
      }
    }

    if (somethingChanged) {
      this.dispatchEvent({
        type: 'classification_visibility_changed',
        viewer: this,
      });
    }
  }

  setFilterReturnNumberRange(from, to) {
    this.filterReturnNumberRange = [from, to];
    this.dispatchEvent({
      type: 'filter_return_number_range_changed',
      viewer: this,
    });
  }

  setFilterNumberOfReturnsRange(from, to) {
    this.filterNumberOfReturnsRange = [from, to];
    this.dispatchEvent({
      type: 'filter_number_of_returns_range_changed',
      viewer: this,
    });
  }

  setFilterGPSTimeRange(from, to) {
    this.filterGPSTimeRange = [from, to];
    this.dispatchEvent({ type: 'filter_gps_time_range_changed', viewer: this });
  }

  setFilterPointSourceIDRange(from, to) {
    this.filterPointSourceIDRange = [from, to];
    this.dispatchEvent({
      type: 'filter_point_source_id_range_changed',
      viewer: this,
    });
  }

  setLengthUnit(value) {
    switch (value) {
      case 'm':
        this.lengthUnit = LengthUnits.METER;
        this.lengthUnitDisplay = LengthUnits.METER;
        break;
      case 'ft':
        this.lengthUnit = LengthUnits.FEET;
        this.lengthUnitDisplay = LengthUnits.FEET;
        break;
      case 'in':
        this.lengthUnit = LengthUnits.INCH;
        this.lengthUnitDisplay = LengthUnits.INCH;
        break;
    }

    this.dispatchEvent({
      type: 'length_unit_changed',
      viewer: this,
      value: value,
    });
  }

  setLengthUnitAndDisplayUnit(lengthUnitValue, lengthUnitDisplayValue) {
    switch (lengthUnitValue) {
      case 'm':
        this.lengthUnit = LengthUnits.METER;
        break;
      case 'ft':
        this.lengthUnit = LengthUnits.FEET;
        break;
      case 'in':
        this.lengthUnit = LengthUnits.INCH;
        break;
    }

    switch (lengthUnitDisplayValue) {
      case 'm':
        this.lengthUnitDisplay = LengthUnits.METER;
        break;
      case 'ft':
        this.lengthUnitDisplay = LengthUnits.FEET;
        break;
      case 'in':
        this.lengthUnitDisplay = LengthUnits.INCH;
        break;
    }

    this.dispatchEvent({
      type: 'length_unit_changed',
      viewer: this,
      value: lengthUnitValue,
    });
  }

  zoomTo(node, factor, animationDuration = 0) {
    let view = this.scene.view;

    let camera = this.scene.cameraP.clone();
    camera.rotation.copy(this.scene.cameraP.rotation);
    camera.rotation.order = 'ZXY';
    camera.rotation.x = Math.PI / 2 + view.pitch;
    camera.rotation.z = view.yaw;
    camera.updateMatrix();
    camera.updateMatrixWorld();
    camera.zoomTo(node, factor);

    let bs;
    if (node.boundingSphere) {
      bs = node.boundingSphere;
    } else if (node.geometry && node.geometry.boundingSphere) {
      bs = node.geometry.boundingSphere;
    } else {
      bs = node.boundingBox.getBoundingSphere(new THREE.Sphere());
    }
    bs = bs.clone().applyMatrix4(node.matrixWorld);

    let startPosition = view.position.clone();
    let endPosition = camera.position.clone();
    let startTarget = view.getPivot();
    let endTarget = bs.center;
    let startRadius = view.radius;
    let endRadius = endPosition.distanceTo(endTarget);

    let easing = TWEEN.Easing.Quartic.Out;

    {
      // animate camera position
      let pos = startPosition.clone();
      let tween = new TWEEN.Tween(pos).to(endPosition, animationDuration);
      tween.easing(easing);

      tween.onUpdate(() => {
        view.position.copy(pos);
      });

      tween.start();
    }

    {
      // animate camera target
      let target = startTarget.clone();
      let tween = new TWEEN.Tween(target).to(endTarget, animationDuration);
      tween.easing(easing);
      tween.onUpdate(() => {
        view.lookAt(target);
      });
      tween.onComplete(() => {
        view.lookAt(target);
        this.dispatchEvent({ type: 'focusing_finished', target: this });
      });

      this.dispatchEvent({ type: 'focusing_started', target: this });
      tween.start();
    }
  }

  moveToGpsTimeVicinity(time) {
    const result = Potree.Utils.findClosestGpsTime(time, viewer);

    const box = result.node.pointcloud
      .deepestNodeAt(result.position)
      .getBoundingBox();
    const diameter = box.min.distanceTo(box.max);

    const camera = this.scene.getActiveCamera();
    const offset = camera
      .getWorldDirection(new THREE.Vector3())
      .multiplyScalar(diameter);
    const newCamPos = result.position.clone().sub(offset);

    this.scene.view.position.copy(newCamPos);
    this.scene.view.lookAt(result.position);
  }

  showAbout() {
    $(function () {
      $('#about-panel').dialog();
    });
  }

  getBoundingBox(pointclouds) {
    return this.scene.getBoundingBox(pointclouds);
  }

  getGpsTimeExtent() {
    const range = [Infinity, -Infinity];

    for (const pointcloud of this.scene.pointclouds) {
      const attributes = pointcloud.pcoGeometry.pointAttributes.attributes;
      const aGpsTime = attributes.find((a) => a.name === 'gps-time');

      if (aGpsTime) {
        range[0] = Math.min(range[0], aGpsTime.range[0]);
        range[1] = Math.max(range[1], aGpsTime.range[1]);
      }
    }

    return range;
  }

  fitToScreen(factor = 1, animationDuration = 0) {
    let box = this.getBoundingBox(this.scene.pointclouds);

    let node = new THREE.Object3D();
    node.boundingBox = box;

    this.zoomTo(node, factor, animationDuration);
    this.controls.stop();
  }

  toggleNavigationCube() {
    this.navigationCube.visible = !this.navigationCube.visible;
  }

  setView(view) {
    if (!view) return;

    switch (view) {
      case 'F':
        this.setFrontView();
        break;
      case 'B':
        this.setBackView();
        break;
      case 'L':
        this.setLeftView();
        break;
      case 'R':
        this.setRightView();
        break;
      case 'U':
        this.setTopView();
        break;
      case 'D':
        this.setBottomView();
        break;
    }
  }

  setTopView() {
    this.scene.view.yaw = 0;
    this.scene.view.pitch = -Math.PI / 2;

    this.fitToScreen();
  }

  setBottomView() {
    this.scene.view.yaw = -Math.PI;
    this.scene.view.pitch = Math.PI / 2;

    this.fitToScreen();
  }

  setFrontView() {
    this.scene.view.yaw = 0;
    this.scene.view.pitch = 0;

    this.fitToScreen();
  }

  setBackView() {
    this.scene.view.yaw = Math.PI;
    this.scene.view.pitch = 0;

    this.fitToScreen();
  }

  setLeftView() {
    this.scene.view.yaw = -Math.PI / 2;
    this.scene.view.pitch = 0;

    this.fitToScreen();
  }

  setRightView() {
    this.scene.view.yaw = Math.PI / 2;
    this.scene.view.pitch = 0;

    this.fitToScreen();
  }

  flipYZ() {
    this.isFlipYZ = !this.isFlipYZ;

    // TODO flipyz
  }

  setCameraMode(mode) {
    this.scene.cameraMode = mode;

    for (let pointcloud of this.scene.pointclouds) {
      pointcloud.material.useOrthographicCamera =
        mode == CameraMode.ORTHOGRAPHIC;
    }
  }

  /**
   * Authoritative scene CRS from the host app (site / location EPSG).
   * When set, getProjection() prefers this over any pointcloud so a local
   * cloud at pointclouds[0] cannot drag the minimap into local mode.
   */
  setSiteProjection(projection) {
    const p = projection == null ? null : String(projection).trim();
    this.siteProjection = p && p.length > 0 ? p : null;
  }

  getProjection() {
    if (this.siteProjection) {
      return this.siteProjection;
    }
    // Prefer the first cloud that actually declares a CRS — not [0], which
    // is often a local dataset in mixed georef+local sites.
    for (const pointcloud of this.scene.pointclouds) {
      const p = pointcloud?.projection;
      if (typeof p === 'string' && p.trim() !== '') {
        return p;
      }
    }
    return null;
  }

  async loadProject(urlOrJson) {
    if (typeof urlOrJson === 'object' && urlOrJson !== null && urlOrJson.type === 'Potree') {
      Potree.loadProject(viewer, urlOrJson);
      return;
    }

    // Zakładamy, że to URL do JSONa
    const response = await fetch(urlOrJson);
    const text = await response.text();
    const json = JSON5.parse(text);

    if (json.type === 'Potree') {
      Potree.loadProject(viewer, json);
    }
  }

  saveProject() {
    return Potree.saveProject(this);
  }

  loadSettingsFromURL() {
    if (Utils.getParameterByName('pointSize')) {
      this.setPointSize(parseFloat(Utils.getParameterByName('pointSize')));
    }

    if (Utils.getParameterByName('FOV')) {
      this.setFOV(parseFloat(Utils.getParameterByName('FOV')));
    }

    if (Utils.getParameterByName('opacity')) {
      this.setOpacity(parseFloat(Utils.getParameterByName('opacity')));
    }

    if (Utils.getParameterByName('edlEnabled')) {
      let enabled = Utils.getParameterByName('edlEnabled') === 'true';
      this.setEDLEnabled(enabled);
    }

    if (Utils.getParameterByName('edlRadius')) {
      this.setEDLRadius(parseFloat(Utils.getParameterByName('edlRadius')));
    }

    if (Utils.getParameterByName('edlStrength')) {
      this.setEDLStrength(parseFloat(Utils.getParameterByName('edlStrength')));
    }

    if (Utils.getParameterByName('pointBudget')) {
      this.setPointBudget(parseFloat(Utils.getParameterByName('pointBudget')));
    }

    if (Utils.getParameterByName('showBoundingBox')) {
      let enabled = Utils.getParameterByName('showBoundingBox') === 'true';
      if (enabled) {
        this.setShowBoundingBox(true);
      } else {
        this.setShowBoundingBox(false);
      }
    }

    if (Utils.getParameterByName('material')) {
      let material = Utils.getParameterByName('material');
      this.setMaterial(material);
    }

    if (Utils.getParameterByName('pointSizing')) {
      let sizing = Utils.getParameterByName('pointSizing');
      this.setPointSizing(sizing);
    }

    if (Utils.getParameterByName('quality')) {
      let quality = Utils.getParameterByName('quality');
      this.setQuality(quality);
    }

    if (Utils.getParameterByName('position')) {
      let value = Utils.getParameterByName('position');
      value = value.replace('[', '').replace(']', '');
      let tokens = value.split(';');
      let x = parseFloat(tokens[0]);
      let y = parseFloat(tokens[1]);
      let z = parseFloat(tokens[2]);

      this.scene.view.position.set(x, y, z);
    }

    if (Utils.getParameterByName('target')) {
      let value = Utils.getParameterByName('target');
      value = value.replace('[', '').replace(']', '');
      let tokens = value.split(';');
      let x = parseFloat(tokens[0]);
      let y = parseFloat(tokens[1]);
      let z = parseFloat(tokens[2]);

      this.scene.view.lookAt(new THREE.Vector3(x, y, z));
    }

    if (Utils.getParameterByName('background')) {
      let value = Utils.getParameterByName('background');
      this.setBackground(value);
    }

    // if(Utils.getParameterByName("elevationRange")){
    //	let value = Utils.getParameterByName("elevationRange");
    //	value = value.replace("[", "").replace("]", "");
    //	let tokens = value.split(";");
    //	let x = parseFloat(tokens[0]);
    //	let y = parseFloat(tokens[1]);
    //
    //	this.setElevationRange(x, y);
    //	//this.scene.view.target.set(x, y, z);
    // }
  }

  // ------------------------------------------------------------------------------------
  // Viewer Internals
  // ------------------------------------------------------------------------------------

  createControls() {
    {
      // create FIRST PERSON CONTROLS
      this.fpControls = new FirstPersonControls(this);
      this.fpControls.enabled = false;
      this.fpControls.addEventListener(
        'start',
        this.disableAnnotations.bind(this)
      );
      this.fpControls.addEventListener(
        'end',
        this.enableAnnotations.bind(this)
      );
    }

    // { // create GEO CONTROLS
    //	this.geoControls = new GeoControls(this.scene.camera, this.renderer.domElement);
    //	this.geoControls.enabled = false;
    //	this.geoControls.addEventListener("start", this.disableAnnotations.bind(this));
    //	this.geoControls.addEventListener("end", this.enableAnnotations.bind(this));
    //	this.geoControls.addEventListener("move_speed_changed", (event) => {
    //		this.setMoveSpeed(this.geoControls.moveSpeed);
    //	});
    // }

    {
      // create ORBIT CONTROLS
      this.orbitControls = new OrbitControls(this);
      this.orbitControls.enabled = false;
      this.orbitControls.addEventListener(
        'start',
        this.disableAnnotations.bind(this)
      );
      this.orbitControls.addEventListener(
        'end',
        this.enableAnnotations.bind(this)
      );
    }

    {
      // create EARTH CONTROLS
      this.earthControls = new EarthControls(this);
      this.earthControls.enabled = false;
      this.earthControls.addEventListener(
        'start',
        this.disableAnnotations.bind(this)
      );
      this.earthControls.addEventListener(
        'end',
        this.enableAnnotations.bind(this)
      );
    }

    {
      // create DEVICE ORIENTATION CONTROLS
      this.deviceControls = new DeviceOrientationControls(this);
      this.deviceControls.enabled = false;
      this.deviceControls.addEventListener(
        'start',
        this.disableAnnotations.bind(this)
      );
      this.deviceControls.addEventListener(
        'end',
        this.enableAnnotations.bind(this)
      );
    }

    {
      // create VR CONTROLS
      this.vrControls = new VRControls(this);
      this.vrControls.enabled = false;
      this.vrControls.addEventListener(
        'start',
        this.disableAnnotations.bind(this)
      );
      this.vrControls.addEventListener(
        'end',
        this.enableAnnotations.bind(this)
      );
    }
  }

  toggleSidebar() {
    let sidebar = $('#potree_sidebar_container');
    let isVisible = sidebar.is(':visible');
    this.sidebarVisible = !isVisible;
    if (isVisible) {
      sidebar.hide();
    } else {
      sidebar.show();
    }
  }

  toggleMap() {
    // let map = $('#potree_map');
    // map.toggle(100);

    if (this.mapView) {
      this.mapView.toggle();
    }
  }

  onGUILoaded(callback) {
    if (this.guiLoaded) {
      callback();
    } else {
      this.guiLoadTasks.push(callback);
    }
  }

  promiseGuiLoaded() {
    return new Promise((resolve) => {
      if (this.guiLoaded) {
        resolve();
      } else {
        this.guiLoadTasks.push(resolve);
      }
    });
  }

  /** Invalidate in-flight loadGUI fetches (call on host unmount). */
  cancelGuiLoad() {
    this._guiLoadGeneration = (this._guiLoadGeneration || 0) + 1;
    this.sidebar?.cancelTopbarLoad?.();
  }

  /**
   * Stop this viewer for good: halt the render loop, evict its point clouds
   * from the shared node cache, release the WebGL context and clear the DOM
   * it owns. A host that swaps viewers (one per site/view) must call this on
   * the instance it abandons — otherwise the old loop keeps running
   * update() + EDL passes every frame for a detached canvas, its octree
   * keeps streaming nodes, and every leaked context counts toward the
   * browser's per-page WebGL limit (Chrome drops the oldest context past 16,
   * which stalls the live viewer while it recompiles and re-uploads).
   * Idempotent; the instance is unusable afterwards.
   */
  dispose() {
    if (this._disposed) return;
    this._disposed = true;

    this.cancelGuiLoad();
    this.renderer.setAnimationLoop(null);

    try {
      this.clearMultiViewLayout();
    } catch (e) { /* ignore */ }

    // Node geometries live in the global Potree.lru; without this they stay
    // resident until the point-load limit evicts them, long after the cloud
    // is gone.
    for (const pointcloud of this.scene.pointclouds) {
      const root = pointcloud.pcoGeometry?.root;
      if (!root) continue;
      try {
        Potree.lru.disposeDescendants(root);
      } catch (e) { /* best effort */ }
    }
    this.scene.pointclouds.length = 0;

    try {
      this.renderer.dispose();
      this.renderer.forceContextLoss();
    } catch (e) { /* ignore */ }
    $(this.renderArea).empty();

    for (const fn of this._disposers.splice(0)) {
      try {
        fn();
      } catch (e) { /* best effort */ }
    }
  }

  /**
   * Registers a teardown callback that dispose() runs once. Use it for
   * anything that outlives $(renderArea).empty(): nodes appended to body,
   * document/window listeners, intervals. Runs immediately if the viewer is
   * already disposed, so a late async init cannot leak past dispose().
   */
  onDispose(fn) {
    if (this._disposed) {
      fn();
      return;
    }
    this._disposers.push(fn);
  }

  loadGUI(callback) {
    const generation = this._guiLoadGeneration;
    const isStale = () => this._guiLoadGeneration !== generation;

    if (callback) {
      this.onGUILoaded(() => {
        if (isStale()) return;
        callback();
      });
    }

    // Detect mobile device
    const isMobile = () => {
      return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
        (typeof window.orientation !== 'undefined') ||
        (window.matchMedia && window.matchMedia('(max-width: 768px)').matches) ||
        ('ontouchstart' in window && navigator.maxTouchPoints > 1);
    };

    let viewer = this;
    let sidebarContainer = $('#potree_sidebar_container');

    // Load mobile toolbar instead of sidebar on mobile devices
    if (isMobile()) {
      // `callback` is already queued via onGUILoaded above; the toolbar only
      // has to mark the GUI loaded once its template is ready.
      this.loadMobileToolbar({ isStale });
      return this.promiseGuiLoaded();
    }

    sidebarContainer.load(
      new URL(`${Potree.scriptPath}/sidebar.html?v=${encodeURIComponent(Potree.assetCacheKey)}`).href,
      () => {
        if (isStale()) return;

        // Sidebar sizes itself to content (thin toolbar)

        // Restore saved theme preference
        const savedTheme = localStorage.getItem('potree-theme') || 'light';
        if (savedTheme === 'dark') {
          document.documentElement.setAttribute('data-theme', 'dark');
          const bg = this.getBackground();
          if (bg === 'gradient' || bg === 'gradient-light') this.setBackground('gradient');
        } else {
          const bg = this.getBackground();
          if (bg === 'gradient' || bg === 'gradient-light') this.setBackground('gradient-light');
        }

        // Sidebar is always visible — no toggle button needed

        const vrButton = VRButton.createButton(this.renderer);
        vrButton.isWorking = false;
        if (vrButton.isWorking) {
          // włącz tryb XR w rendererze
          this.renderer.xr.enabled = true;

          const element = vrButton;

          element.style.position = '';
          element.style.bottom = '';
          element.style.left = '';
          element.style.margin = '4px';
          element.style.fontSize = '100%';
          element.style.width = '2.5em';
          element.style.height = '2.5em';
          element.style.padding = '0';
          element.style.textShadow = 'black 2px 2px 2px';
          element.style.display = 'block';

          elButtons.append(element);

          this.renderer.xr.addEventListener('sessionstart', () => {
            this.dispatchEvent({ type: 'vr_start' });
          });
          this.renderer.xr.addEventListener('sessionend', () => {
            this.dispatchEvent({ type: 'vr_end' });
          });
        }

        this.mapView = new MapView(this);
        this.mapView.init();

        i18n.init(
          {
            lng: localStorage.getItem('potree-language') || 'en',
            fallbackLng: 'en',
            resGetPath: Potree.resourcePath + '/lang/__lng__/__ns__.json',
            preload: [
              'en',
              'pl',
            ],
            getAsync: true,
            debug: false,
          },
          () => {
            if (isStale()) return;
            // Start translation once everything is loaded
            $('body').i18n();
          }
        );

        $(() => {
          if (isStale()) return;

          //initSidebar(this);
          let sidebar = new Sidebar(this);
          sidebar.init();

          this.sidebar = sidebar;

          //if (callback) {
          //	$(callback);
          //}

          let elProfile = $('<div>').load(
            new URL(`${Potree.scriptPath}/profile.html?v=${encodeURIComponent(Potree.assetCacheKey)}`).href,
            () => {
              if (isStale()) return;
              $(document.body).append(elProfile.children());
              this.profileWindow = new ProfileWindow(this);
              this.profileWindowController = new ProfileWindowController(this);

              $('#profile_window').draggable({
                handle: $('#profile_titlebar'),
                containment: $(document.body),
              });
              $('#profile_window').resizable({
                containment: $(document.body),
                handles: 'n, e, s, w',
              });

              $(() => {
                if (isStale()) return;
                markGuiLoaded(this);
              });
            }
          );
        });
      }
    );

    return this.promiseGuiLoaded();
  }

  /**
   * @param {{isStale?: () => boolean}} [options] `isStale` comes from loadGUI's
   *   generation counter: once cancelGuiLoad()/dispose() bumped it, a late
   *   i18n init from the abandoned viewer must not mark the GUI loaded — the
   *   queued host callback would otherwise fire for a viewer that no longer
   *   owns the scene (StrictMode remount: clouds got added to the new viewer
   *   a second time).
   */
  loadMobileToolbar({ isStale = () => false } = {}) {
    let viewer = this;
    let renderArea = $(this.renderArea);

    // ========================================================================
    // MOBILE UI SYSTEM - 3 switchable variants
    // Use window.switchMobileUI('minimal'), window.switchMobileUI('bottom'), 
    // or window.switchMobileUI('fab') in console to switch between them
    // ========================================================================

    // Load saved point budget from localStorage
    let hasSavedBudget = false;
    try {
      const savedBudget = localStorage.getItem('potree_mobile_point_budget');
      if (savedBudget) {
        const budgetValue = parseInt(savedBudget);
        if (!isNaN(budgetValue) && budgetValue > 0) {
          viewer.setPointBudget(budgetValue);
          hasSavedBudget = true;
        } else {
          console.warn('[localStorage] Invalid point budget value:', savedBudget);
        }
      }
    } catch (e) {
      console.warn('[localStorage] Failed to load point budget:', e);
    }

    // Set default point budget for mobile only if no saved value exists
    let calculatedBudget = null;
    if (!hasSavedBudget) {
      calculatedBudget = viewer.getOptimalMobilePointBudget();
      viewer.setPointBudget(calculatedBudget);
    }

    // Display message about calculated budget after i18n is initialized
    if (calculatedBudget !== null) {
      setTimeout(() => {
        const i18nInstance = (typeof i18n !== 'undefined' ? i18n : window.i18n);
        const message = i18nInstance.t('mobile.point_budget_calculated', { value: calculatedBudget.toLocaleString() });
        viewer.postMessage(message, { duration: 4000 });
      }, 1500); // Delay to ensure i18n is loaded
    }

    // Language will be loaded after i18n.init() - see below in the init callback

    // Load saved UI variant from localStorage
    let savedUIVariant = 'minimal'; // Default: 'minimal', 'bottom', or 'fab'
    try {
      const saved = localStorage.getItem('potree_mobile_ui_variant');
      if (saved && ['minimal', 'bottom', 'fab'].includes(saved)) {
        savedUIVariant = saved;
      }
    } catch (e) {
      console.warn('[localStorage] Failed to load UI variant:', e);
    }

    let currentUIVariant = savedUIVariant; // Use saved variant or default to 'minimal'
    let measureModeActive = false;

    // Initialize i18n for translations
    const i18nInstance = (typeof i18n !== 'undefined' ? i18n : window.i18n);

    // Shared state
    const state = {
      measureModeActive: false,
      selectedMeasurement: null,
      measurementPaused: false,
      pausedMeasurement: null,
      selectedTool: null // Track currently selected tool for highlighting
    };

    // Measurement tool definitions (labels are i18n keys)
    const measureTools = [
      { name: 'point', icon: 'point.svg', labelKey: 'mobile.tool_point', options: { showDistances: false, showAngles: false, showCoordinates: true, showArea: false, closed: true, maxMarkers: 1, name: 'Point' } },
      { name: 'distance', icon: 'distance.svg', labelKey: 'mobile.tool_distance', options: { showDistances: true, showArea: false, closed: false, name: 'Distance' } },
      { name: 'height', icon: 'height.svg', labelKey: 'mobile.tool_height', options: { showDistances: false, showHeight: true, maxMarkers: 2, name: 'Height' } },
      { name: 'angle', icon: 'angle.png', labelKey: 'mobile.tool_angle', options: { showDistances: true, showAngles: true, maxMarkers: 3, closed: true, name: 'Angle' } },
      { name: 'area', icon: 'area.svg', labelKey: 'mobile.tool_area', options: { showDistances: true, showArea: true, closed: true, name: 'Area' } },
      { name: 'circle', icon: 'circle.svg', labelKey: 'mobile.tool_circle', options: { showDistances: false, showCircle: true, maxMarkers: 3, name: 'Circle' } },
      { name: 'azimuth', icon: 'azimuth.svg', labelKey: 'mobile.tool_azimuth', options: { showDistances: false, showAzimuth: true, maxMarkers: 2, name: 'Azimuth' } },
      { name: 'annotation', icon: 'annotation.svg', labelKey: 'mobile.tool_note', isAnnotation: true }
    ];

    // Helper to get translated label
    const getToolLabel = (tool) => {
      return i18nInstance.t(tool.labelKey) || tool.labelKey.split('.').pop();
    };

    // Helper: activate measure mode
    const activateMeasureMode = () => {
      state.measureModeActive = true;
      viewer.measureModeEnabled = true;
      viewer.inputHandler.measureModeEnabled = true;
    };

    // Helper: deactivate measure mode
    const deactivateMeasureMode = () => {
      state.measureModeActive = false;
      state.measurementPaused = false;
      state.pausedMeasurement = null;
      viewer.measureModeEnabled = false;
      viewer.inputHandler.measureModeEnabled = false;
      viewer.dispatchEvent({ type: 'cancel_insertions' });
    };

    // Helper: pause measurement (allow navigation while keeping measurement active)
    // This mimics the Ctrl pause behavior from MeasuringTool
    const pauseMeasurement = () => {
      if (!state.measureModeActive) return;

      // Get current measurement from state (set in startMeasurement)
      // or find it in the scene as fallback
      const currentMeasure = state.pausedMeasurement ||
        (viewer.measuringTool.scene.children.find(child => {
          // Check if it's a Measure instance with points
          return child instanceof Measure && child.points && child.points.length > 0 &&
            child.spheres && child.spheres.length > 0;
        }));

      if (currentMeasure) {
        state.measurementPaused = true;
        state.pausedMeasurement = currentMeasure;

        // Set currentMeasurement in MeasuringTool (same as Ctrl does in insertionCallback)
        // This is the key: currentMeasurement is set ONLY when pausing, not during active insertion
        viewer.measuringTool.currentMeasurement = currentMeasure;

        // Temporarily disable measure mode to allow navigation (mobile-specific)
        viewer.measureModeEnabled = false;
        viewer.inputHandler.measureModeEnabled = false;

        // Cancel current insertion callbacks (stops adding new points)
        // This is equivalent to cancel.callback() in Ctrl logic
        viewer.dispatchEvent({ type: 'cancel_insertions' });

      } else {
        console.warn('[pauseMeasurement] No active measurement found to pause');
      }
    };

    // Helper: resume measurement (continue adding points)
    // This mimics the Ctrl resume behavior from MeasuringTool constructor
    const resumeMeasurement = () => {
      if (!state.measurementPaused) {
        console.warn('[resumeMeasurement] Cannot resume - not paused');
        return;
      }

      // Get measure from MeasuringTool.currentMeasurement (set during pause)
      const measure = viewer.measuringTool.currentMeasurement || state.pausedMeasurement;

      if (!measure) {
        console.warn('[resumeMeasurement] Cannot resume - no measurement reference');
        state.measurementPaused = false;
        return;
      }

      // Check if measurement can be continued (not Point, Angle, Height, Circle, Azimuth, Volume)
      if (measure.name === 'Point' || measure.name === 'Angle' || measure.name === 'Height' ||
        measure.name === 'Circle' || measure.name === 'Azimuth' ||
        measure instanceof Volume) {
        console.warn('[resumeMeasurement] Cannot continue insertion for', measure.name);
        // Just re-enable measure mode but don't continue insertion - measurement is finished
        viewer.measureModeEnabled = true;
        viewer.inputHandler.measureModeEnabled = true;
        state.measurementPaused = false;
        state.pausedMeasurement = null;
        viewer.measuringTool.currentMeasurement = null;
        return;
      }

      // Re-enable measure mode (mobile-specific, allows touch measurement)
      viewer.measureModeEnabled = true;
      viewer.inputHandler.measureModeEnabled = true;

      // Continue insertion for the paused measurement (same as Ctrl does)
      try {
        viewer.measuringTool.continueInsertion(measure);

        // Reset currentMeasurement to null (same as Ctrl does after continueInsertion)
        viewer.measuringTool.currentMeasurement = null;

        state.measurementPaused = false;
        // Keep pausedMeasurement reference for potential future pauses

      } catch (error) {
        console.error('[resumeMeasurement] Error continuing insertion:', error);
        // Fallback: just re-enable measure mode
        viewer.measureModeEnabled = true;
        viewer.inputHandler.measureModeEnabled = true;
        state.measurementPaused = false;
        viewer.measuringTool.currentMeasurement = null;
      }
    };

    // Helper: start measurement
    const startMeasurement = (toolOptions) => {
      activateMeasureMode();
      const measure = viewer.measuringTool.startInsertion(toolOptions);
      // Store reference to current measurement in state (NOT in currentMeasurement - that's for paused state only!)
      if (measure) {
        state.pausedMeasurement = measure;
        // currentMeasurement should be null during active insertion (same as Ctrl logic)
      }
    };

    // Helper: start annotation
    const startAnnotation = () => {
      activateMeasureMode();
      viewer.annotationTool.startInsertion();
    };

    // Helper: update tool highlighting across all UI variants
    const updateToolHighlighting = (tool) => {
      // Remove active class from all tool buttons
      $('.fab-tool-item, .bottom-tool-inline, .bottom-tool-grid, .floating-tool').removeClass('tool-active');

      // Add active class to selected tool buttons
      if (tool) {
        $(`.fab-tool-item[data-tool="${tool.name}"]`).addClass('tool-active');
        $(`.bottom-tool-inline[data-tool="${tool.name}"]`).addClass('tool-active');
        $(`.bottom-tool-grid[data-tool="${tool.name}"]`).addClass('tool-active');
        $(`.floating-tool[data-tool="${tool.name}"]`).addClass('tool-active');
      }
    };

    // Helper: update selected tool indicator on menu buttons
    const updateSelectedToolIndicator = (tool) => {
      // Remove existing indicators
      $('.selected-tool-indicator').remove();

      if (tool) {
        const iconUrl = `${Potree.resourcePath}/icons/${tool.icon}`;
        const indicator = $(`<div class="selected-tool-indicator"><img src="${iconUrl}" /></div>`);

        // Add indicator to FAB main button
        $('#fab_main').css('position', 'relative').append(indicator.clone());

        // For minimal UI, use the same badge indicator as FAB (on the button itself)
        $('#floating_btn').css('position', 'relative').append(indicator.clone());

        // Update mini icon in floating menu header
        const floatingMenuSelectedTool = $('#floating_menu_selected_tool');
        const floatingMenuSelectedToolImg = $('#floating_menu_selected_tool_img');
        const floatingMenuSelectedToolLabel = $('#floating_menu_selected_tool_label');
        if (floatingMenuSelectedTool.length && floatingMenuSelectedToolImg.length && floatingMenuSelectedToolLabel.length) {
          floatingMenuSelectedToolImg.attr('src', iconUrl);
          floatingMenuSelectedToolLabel.text(getToolLabel(tool));
          floatingMenuSelectedTool.css('display', 'flex');
        }

        // Add indicator to bottom mode toggle button
        $('#bottom_mode_toggle').css('position', 'relative').append(indicator.clone());
      } else {
        // Hide mini icon in floating menu header
        $('#floating_menu_selected_tool').css('display', 'none');
      }
    };

    // Helper: handle tool click (measurement or annotation)
    const handleToolClick = (tool) => {
      // Set selected tool for highlighting
      state.selectedTool = tool;
      updateToolHighlighting(tool);
      updateSelectedToolIndicator(tool);

      if (tool.isAnnotation) {
        startAnnotation();
      } else {
        startMeasurement(tool.options);
      }
    };

    // Helper: check if measurement is actively being inserted (not finished)
    const isInsertionActive = () => {
      // Check if we have an active measurement that can still accept points
      const measure = state.pausedMeasurement;
      if (!measure) return false;

      // Check if measurement is finished (reached maxMarkers)
      if (measure.maxMarkers !== Infinity && measure.points.length >= measure.maxMarkers) {
        return false;
      }

      // Check if it's a type that cannot be continued
      if (measure.name === 'Point' || measure.name === 'Angle' || measure.name === 'Height' ||
        measure.name === 'Circle' || measure.name === 'Azimuth') {
        // These types have fixed number of points - check if finished
        if (measure.name === 'Point' && measure.points.length >= 1) return false;
        if (measure.name === 'Height' && measure.points.length >= 2) return false;
        if (measure.name === 'Azimuth' && measure.points.length >= 2) return false;
        if (measure.name === 'Angle' && measure.points.length >= 3) return false;
        if (measure.name === 'Circle' && measure.points.length >= 3) return false;
      }

      return true;
    };

    // Helper: update pause/resume button appearance based on state
    // Liquid Glass style gradients for pause (amber) and resume (green)
    const pauseGradient = 'linear-gradient(135deg, rgba(251,191,36,0.4) 0%, rgba(251,191,36,0.2) 50%, rgba(251,191,36,0.3) 100%)';
    const pauseShadow = '0 4px 24px rgba(251,191,36,0.25), inset 0 1px 1px rgba(255,255,255,0.4), inset 0 -1px 2px rgba(0,0,0,0.1)';
    const pauseBorder = '1px solid rgba(251,191,36,0.5)';

    const resumeGradient = 'linear-gradient(135deg, rgba(34,197,94,0.4) 0%, rgba(34,197,94,0.2) 50%, rgba(34,197,94,0.3) 100%)';
    const resumeShadow = '0 4px 24px rgba(34,197,94,0.25), inset 0 1px 1px rgba(255,255,255,0.4), inset 0 -1px 2px rgba(0,0,0,0.1)';
    const resumeBorder = '1px solid rgba(34,197,94,0.5)';

    const updatePauseResumeButton = (variant) => {
      // Only show button if insertion is actively happening
      const insertionActive = isInsertionActive();

      if (variant === 'fab') {
        const btn = $('#fab_pause_resume');
        if (state.measureModeActive && !state.measurementPaused && insertionActive) {
          // Show pause button
          btn.css('display', 'flex').html(svgIcons.pause);
          btn.css({ 'background': pauseGradient, 'box-shadow': pauseShadow, 'border': pauseBorder });
        } else if (state.measurementPaused && insertionActive) {
          // Show resume button
          btn.css('display', 'flex').html(svgIcons.play);
          btn.css({ 'background': resumeGradient, 'box-shadow': resumeShadow, 'border': resumeBorder });
        } else {
          // Hide button
          btn.hide();
        }
      } else if (variant === 'bottom') {
        const btn = $('#bottom_pause_resume');
        if (state.measureModeActive && !state.measurementPaused && insertionActive) {
          btn.css('display', 'flex').html(svgIcons.pause);
          btn.css({ 'background': pauseGradient, 'box-shadow': pauseShadow, 'border': pauseBorder });
        } else if (state.measurementPaused && insertionActive) {
          btn.css('display', 'flex').html(svgIcons.play);
          btn.css({ 'background': resumeGradient, 'box-shadow': resumeShadow, 'border': resumeBorder });
        } else {
          btn.hide();
        }
      } else if (variant === 'minimal') {
        const btn = $('#floating_pause_resume');
        if (state.measureModeActive && !state.measurementPaused && insertionActive) {
          // Show pause button next to menu icon
          btn.css('display', 'flex').html(svgIcons.pause);
          btn.css({ 'background': pauseGradient, 'box-shadow': pauseShadow, 'border': pauseBorder });
        } else if (state.measurementPaused && insertionActive) {
          // Show resume button next to menu icon
          btn.css('display', 'flex').html(svgIcons.play);
          btn.css({ 'background': resumeGradient, 'box-shadow': resumeShadow, 'border': resumeBorder });
        } else {
          // Hide button when measurement is not active
          btn.hide();
        }
      }
    };

    // Add SVG filters for True Liquid Glass effect
    if (!$('#liquid-glass-svg-filters').length) {
      $('body').prepend(`
        <svg id="liquid-glass-svg-filters" style="position: absolute; width: 0; height: 0; pointer-events: none;">
          <defs>
            <!-- Lens distortion filter -->
            <filter id="liquid-glass-lens" x="-50%" y="-50%" width="200%" height="200%" color-interpolation-filters="sRGB">
              <feTurbulence type="fractalNoise" baseFrequency="0.012" numOctaves="3" seed="5" result="noise"/>
              <feDisplacementMap in="SourceGraphic" in2="noise" scale="6" xChannelSelector="R" yChannelSelector="G" result="displaced"/>
              <feGaussianBlur in="displaced" stdDeviation="0.3" result="blur"/>
              <feBlend in="SourceGraphic" in2="blur" mode="normal"/>
            </filter>
            
            <!-- Chromatic aberration filter -->
            <filter id="liquid-glass-chromatic" x="-10%" y="-10%" width="120%" height="120%" color-interpolation-filters="sRGB">
              <!-- Red channel - shift left -->
              <feOffset in="SourceGraphic" dx="-1" dy="0" result="red-shift"/>
              <feColorMatrix in="red-shift" type="matrix" values="1 0 0 0 0  0 0 0 0 0  0 0 0 0 0  0 0 0 1 0" result="red"/>
              <!-- Green channel - no shift -->
              <feColorMatrix in="SourceGraphic" type="matrix" values="0 0 0 0 0  0 1 0 0 0  0 0 0 0 0  0 0 0 1 0" result="green"/>
              <!-- Blue channel - shift right -->
              <feOffset in="SourceGraphic" dx="1" dy="0" result="blue-shift"/>
              <feColorMatrix in="blue-shift" type="matrix" values="0 0 0 0 0  0 0 0 0 0  0 0 1 0 0  0 0 0 1 0" result="blue"/>
              <!-- Combine channels -->
              <feBlend in="red" in2="green" mode="screen" result="rg"/>
              <feBlend in="rg" in2="blue" mode="screen"/>
            </filter>
          </defs>
        </svg>
      `);
    }

    // Add base CSS for all variants
    if (!$('#mobile_ui_base_styles').length) {
      $('<style id="mobile_ui_base_styles">').prop('type', 'text/css').html(`
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes slideUp { from { transform: translateY(100%); } to { transform: translateY(0); } }
        @keyframes scaleIn { from { transform: scale(0); } to { transform: scale(1); } }
        @keyframes pulse { 0%, 100% { transform: scale(1); } 50% { transform: scale(1.05); } }
        
        /* Chromatic shimmer animation for True Liquid Glass */
        @keyframes chromaticShimmer {
          0%, 100% { 
            box-shadow: 
              -1.5px 0 4px rgba(255, 60, 120, 0.2),
              1.5px 0 4px rgba(60, 180, 255, 0.2),
              0 -1px 4px rgba(255, 220, 60, 0.12),
              0 1px 4px rgba(140, 80, 255, 0.12),
              0 4px 24px rgba(0,0,0,0.15), 
              inset 0 1px 1px rgba(255,255,255,0.4), 
              inset 0 -1px 2px rgba(0,0,0,0.1);
          }
          50% { 
            box-shadow: 
              -2px 0 6px rgba(255, 60, 120, 0.3),
              2px 0 6px rgba(60, 180, 255, 0.3),
              0 -1.5px 5px rgba(255, 220, 60, 0.18),
              0 1.5px 5px rgba(140, 80, 255, 0.18),
              0 6px 28px rgba(0,0,0,0.18), 
              inset 0 1px 2px rgba(255,255,255,0.5), 
              inset 0 -1px 2px rgba(0,0,0,0.1);
          }
        }
        
        .mobile-ui-container { 
          font-family: -apple-system, BlinkMacSystemFont, 'SF Pro Display', 'SF Pro Text', 'Inter', system-ui, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
          -webkit-font-smoothing: antialiased;
          -moz-osx-font-smoothing: grayscale;
          text-rendering: optimizeLegibility;
          letter-spacing: -0.01em;
        }
        .mobile-ui-container * { box-sizing: border-box; }
        
        /* ============================================
           LIQUID GLASS - Default Style with Chromatic Aberration
           ============================================ */
        .mobile-tool-btn { 
          touch-action: manipulation; 
          -webkit-tap-highlight-color: transparent;
          user-select: none;
          position: relative;
          background: linear-gradient(135deg, rgba(255,255,255,0.12) 0%, rgba(255,255,255,0.03) 50%, rgba(255,255,255,0.08) 100%) !important;
          backdrop-filter: blur(24px) saturate(200%);
          -webkit-backdrop-filter: blur(24px) saturate(200%);
          border: 1px solid rgba(255, 255, 255, 0.2) !important;
          animation: chromaticShimmer 4s ease-in-out infinite;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        }
        
        .mobile-tool-btn::before {
          content: '';
          position: absolute;
          inset: -1px;
          border-radius: inherit;
          padding: 1.5px;
          background: linear-gradient(
            135deg,
            rgba(255, 80, 120, 0.5) 0%,
            rgba(255, 180, 80, 0.3) 20%,
            rgba(180, 255, 100, 0.3) 40%,
            rgba(80, 220, 255, 0.5) 60%,
            rgba(120, 100, 255, 0.4) 80%,
            rgba(255, 80, 180, 0.4) 100%
          );
          -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
          mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
          -webkit-mask-composite: xor;
          mask-composite: exclude;
          pointer-events: none;
          opacity: 0.8;
          transition: opacity 0.3s ease;
        }
        
        .mobile-tool-btn:hover::before {
          opacity: 1;
        }
        
        .mobile-tool-btn:hover {
          transform: scale(1.08);
          background: linear-gradient(135deg, rgba(255,255,255,0.18) 0%, rgba(255,255,255,0.06) 50%, rgba(255,255,255,0.12) 100%) !important;
          animation: none;
          box-shadow: 
            -2.5px 0 8px rgba(255, 60, 120, 0.4),
            2.5px 0 8px rgba(60, 180, 255, 0.4),
            0 -2px 6px rgba(255, 220, 60, 0.25),
            0 2px 6px rgba(140, 80, 255, 0.25),
            0 8px 32px rgba(0,0,0,0.15), 
            inset 0 1px 2px rgba(255,255,255,0.4), 
            inset 0 -1px 2px rgba(0,0,0,0.05);
        }
        
        .mobile-tool-btn:active { 
          transform: scale(0.95); 
          backdrop-filter: blur(28px) saturate(220%);
          -webkit-backdrop-filter: blur(28px) saturate(220%);
        }
        
        /* Active tool highlighting - Liquid Glass */
        .mobile-tool-btn.tool-active {
          background: linear-gradient(135deg, rgba(34,197,94,0.2) 0%, rgba(34,197,94,0.08) 50%, rgba(34,197,94,0.15) 100%) !important;
          border: 1.5px solid rgba(34, 197, 94, 0.5) !important;
        }
        .mobile-tool-btn.tool-active:hover {
          background: linear-gradient(135deg, rgba(34,197,94,0.28) 0%, rgba(34,197,94,0.12) 50%, rgba(34,197,94,0.2) 100%) !important;
        }
        .mobile-tool-btn.tool-active img {
          filter: brightness(0) invert(1) drop-shadow(0 0 4px rgba(34, 197, 94, 0.8)) !important;
        }

        /* Settings option buttons - Liquid Glass */
        .mobile-settings-option-btn {
          touch-action: manipulation;
          -webkit-tap-highlight-color: transparent;
          position: relative;
          background: linear-gradient(135deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.02) 50%, rgba(255,255,255,0.05) 100%) !important;
          backdrop-filter: blur(16px) saturate(180%);
          -webkit-backdrop-filter: blur(16px) saturate(180%);
          border: 1px solid rgba(255, 255, 255, 0.15) !important;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
        }
        
        .mobile-settings-option-btn::before {
          content: '';
          position: absolute;
          inset: -1px;
          border-radius: inherit;
          padding: 1px;
          background: linear-gradient(
            135deg,
            rgba(255, 80, 120, 0.4) 0%,
            rgba(80, 220, 255, 0.4) 50%,
            rgba(120, 100, 255, 0.4) 100%
          );
          -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
          mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
          -webkit-mask-composite: xor;
          mask-composite: exclude;
          pointer-events: none;
          opacity: 0.6;
          transition: opacity 0.3s ease;
        }
        
        .mobile-settings-option-btn:hover {
          transform: scale(1.03);
          background: linear-gradient(135deg, rgba(255,255,255,0.12) 0%, rgba(255,255,255,0.04) 50%, rgba(255,255,255,0.08) 100%) !important;
        }
        
        .mobile-settings-option-btn:hover::before {
          opacity: 1;
        }
        
        .mobile-settings-option-btn:active {
          transform: scale(0.97);
        }
        
        /* Floating button (minimal UI) - Liquid Glass */
        #floating_btn {
          background: linear-gradient(135deg, rgba(255,255,255,0.12) 0%, rgba(255,255,255,0.03) 50%, rgba(255,255,255,0.08) 100%) !important;
          backdrop-filter: blur(24px) saturate(200%) !important;
          -webkit-backdrop-filter: blur(24px) saturate(200%) !important;
          border: 1px solid rgba(255, 255, 255, 0.2) !important;
          animation: chromaticShimmer 4s ease-in-out infinite;
          box-shadow: 
            -1.5px 0 4px rgba(255, 60, 120, 0.2),
            1.5px 0 4px rgba(60, 180, 255, 0.2),
            0 -1px 4px rgba(255, 220, 60, 0.12),
            0 1px 4px rgba(140, 80, 255, 0.12),
            0 4px 24px rgba(0,0,0,0.15), 
            inset 0 1px 1px rgba(255,255,255,0.4), 
            inset 0 -1px 2px rgba(0,0,0,0.1) !important;
        }
        
        #floating_btn::before {
          content: '';
          position: absolute;
          inset: -1px;
          border-radius: inherit;
          padding: 1.5px;
          background: linear-gradient(
            135deg,
            rgba(255, 80, 120, 0.5) 0%,
            rgba(255, 180, 80, 0.3) 20%,
            rgba(180, 255, 100, 0.3) 40%,
            rgba(80, 220, 255, 0.5) 60%,
            rgba(120, 100, 255, 0.4) 80%,
            rgba(255, 80, 180, 0.4) 100%
          );
          -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
          mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
          -webkit-mask-composite: xor;
          mask-composite: exclude;
          pointer-events: none;
          opacity: 0.8;
          z-index: -1;
        }
        
        /* Selected tool indicator on menu button */
        .selected-tool-indicator {
          position: absolute;
          bottom: -4px;
          right: -4px;
          width: 22px;
          height: 22px;
          border-radius: 50%;
          background: rgba(34, 197, 94, 0.95);
          border: 2px solid white;
          display: flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0 2px 8px rgba(0,0,0,0.4);
          z-index: 10002;
          pointer-events: none;
        }
        .selected-tool-indicator img {
          width: 12px;
          height: 12px;
          filter: brightness(0) invert(1);
        }
        
        /* Ensure floating button can show overflow indicator */
        #floating_btn {
          overflow: visible !important;
        }
        
        /* Axis lock button styles for mobile */
        .axis-lock-btn.axis-locked {
          background: linear-gradient(135deg, rgba(34,197,94,0.3) 0%, rgba(34,197,94,0.15) 50%, rgba(34,197,94,0.25) 100%) !important;
          border: 1.5px solid rgba(34, 197, 94, 0.6) !important;
          color: #22c55e !important;
          font-weight: 700;
          box-shadow: 0 0 8px rgba(34, 197, 94, 0.4);
        }
        .axis-lock-btn.axis-locked:hover {
          background: linear-gradient(135deg, rgba(34,197,94,0.4) 0%, rgba(34,197,94,0.2) 50%, rgba(34,197,94,0.3) 100%) !important;
        }
      `).appendTo('head');
    }

    // SVG icons for mobile UI (universal, consistent across devices)
    const svgIcons = {
      ruler: `<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <path d="M21.3 15.3a2.4 2.4 0 0 1 0 3.4l-2.6 2.6a2.4 2.4 0 0 1-3.4 0L2.7 8.7a2.41 2.41 0 0 1 0-3.4l2.6-2.6a2.41 2.41 0 0 1 3.4 0Z"/>
        <path d="m14.5 12.5 2-2"/><path d="m11.5 9.5 2-2"/><path d="m8.5 6.5 2-2"/><path d="m17.5 15.5 2-2"/>
      </svg>`,
      rulerSmall: `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <path d="M21.3 15.3a2.4 2.4 0 0 1 0 3.4l-2.6 2.6a2.4 2.4 0 0 1-3.4 0L2.7 8.7a2.41 2.41 0 0 1 0-3.4l2.6-2.6a2.41 2.41 0 0 1 3.4 0Z"/>
        <path d="m14.5 12.5 2-2"/><path d="m11.5 9.5 2-2"/><path d="m8.5 6.5 2-2"/><path d="m17.5 15.5 2-2"/>
      </svg>`,
      compass: `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <circle cx="12" cy="12" r="10"/>
        <polygon points="16.24,7.76 14.12,14.12 7.76,16.24 9.88,9.88" fill="currentColor"/>
      </svg>`,
      pause: `<svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
        <rect x="6" y="4" width="4" height="16" rx="1"/>
        <rect x="14" y="4" width="4" height="16" rx="1"/>
      </svg>`,
      play: `<svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
        <polygon points="5,3 19,12 5,21"/>
      </svg>`,
      check: `<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round">
        <polyline points="20,6 9,17 4,12"/>
      </svg>`,
      close: `<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
        <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
      </svg>`,
      drag: `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <path d="M5 9l-3 3 3 3"/><path d="M9 5l3-3 3 3"/><path d="M15 19l-3 3-3-3"/><path d="M19 9l3 3-3 3"/>
        <line x1="2" y1="12" x2="22" y2="12"/><line x1="12" y1="2" x2="12" y2="22"/>
      </svg>`,
      settings: `<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <circle cx="12" cy="12" r="3"/>
        <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"/>
      </svg>`,
      list: `<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/>
        <line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/>
      </svg>`,
      trash: `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
        <line x1="10" y1="11" x2="10" y2="17"/><line x1="14" y1="11" x2="14" y2="17"/>
      </svg>`
    };

    // ========================================================================
    // VARIANT A: FAB (Floating Action Button) - Radial menu
    // Compact circular button, expands into a radial/arc menu
    // ========================================================================
    const createFabUI = () => {
      const container = $(`
        <div id="mobile_ui_fab" class="mobile-ui-container" style="
          position: absolute;
          z-index: 10000;
          bottom: 24px;
          left: 24px;
          display: none;
        ">
          <!-- Container for menu button and measurement controls -->
          <div style="
            display: flex;
            align-items: center;
            gap: 8px;
            position: relative;
            overflow: visible;
          ">
            <!-- Main FAB button -->
            <button id="fab_main" class="mobile-tool-btn" style="
              width: 44px;
              height: 44px;
              border-radius: 50%;
              color: white;
              cursor: pointer;
              display: flex;
              align-items: center;
              justify-content: center;
              position: relative;
              flex-shrink: 0;
              overflow: visible;
            ">
              <span id="fab_main_icon">${svgIcons.ruler}</span>
            </button>
            
            <!-- Pause/Resume button (shown next to button during measurement) -->
            <button id="fab_pause_resume" class="mobile-tool-btn" style="
              width: 44px;
              height: 44px;
              border-radius: 50%;
              color: white;
              cursor: pointer;
              display: none;
              align-items: center;
              justify-content: center;
              flex-shrink: 0;
            ">
              <span id="fab_pause_icon">${svgIcons.pause}</span>
            </button>
            
            <!-- Done button (finish measurement) -->
            <button id="fab_done" class="mobile-tool-btn" style="
              width: 44px;
              height: 44px;
              border-radius: 50%;
              background: rgba(34, 197, 94, 0.9) !important;
              color: white;
              cursor: pointer;
              display: none;
              align-items: center;
              justify-content: center;
              flex-shrink: 0;
            ">${svgIcons.check}</button>
            
            <!-- Axis lock buttons (shown during measurement) -->
            <div id="fab_axis_locks" style="
              display: none;
              gap: 6px;
              align-items: center;
              flex-shrink: 0;
            ">
              <button id="fab_axis_x" class="mobile-tool-btn axis-lock-btn" style="
                width: 36px;
                height: 36px;
                border-radius: 50%;
                color: white;
                cursor: pointer;
                display: flex;
                align-items: center;
                justify-content: center;
                font-size: 14px;
                font-weight: 600;
              ">X</button>
              <button id="fab_axis_y" class="mobile-tool-btn axis-lock-btn" style="
                width: 36px;
                height: 36px;
                border-radius: 50%;
                color: white;
                cursor: pointer;
                display: flex;
                align-items: center;
                justify-content: center;
                font-size: 14px;
                font-weight: 600;
              ">Y</button>
              <button id="fab_axis_z" class="mobile-tool-btn axis-lock-btn" style="
                width: 36px;
                height: 36px;
                border-radius: 50%;
                color: white;
                cursor: pointer;
                display: flex;
                align-items: center;
                justify-content: center;
                font-size: 14px;
                font-weight: 600;
              ">Z</button>
            </div>
          </div>
          
          <!-- Expanded tools (radial layout) -->
          <div id="fab_tools" style="
            position: absolute;
            bottom: 56px;
            left: 0;
            display: none;
            flex-direction: column;
            gap: 12px;
            align-items: flex-start;
          "></div>
        </div>
      `);

      // Build tool buttons
      const toolsContainer = container.find('#fab_tools');
      measureTools.forEach((tool, index) => {
        const btn = $(`
          <button class="mobile-tool-btn fab-tool-item" data-tool="${tool.name}" style="
            width: 44px;
            height: 44px;
            border-radius: 50%;
            cursor: pointer;
            display: flex;
            align-items: center;
            justify-content: center;
            animation: scaleIn 0.2s ease-out ${index * 0.05}s both;
          ">
            <img src="${Potree.resourcePath}/icons/${tool.icon}" style="width: 24px; height: 24px; filter: brightness(0) invert(1);" />
          </button>
        `);
        btn.click(() => {
          handleToolClick(tool);
          container.find('#fab_tools').hide();
          container.find('#fab_main').css('background', 'rgba(34, 197, 94, 0.9)');
          // Show pause and done buttons when measurement starts
          updatePauseResumeButton('fab');
          container.find('#fab_done').css('display', 'flex');
          // Show axis lock buttons
          container.find('#fab_axis_locks').css('display', 'flex');
        });
        toolsContainer.append(btn);
      });

      // Add cancel/close button at the end
      const cancelBtn = $(`
        <button class="mobile-tool-btn" style="
          width: 44px;
          height: 44px;
          border-radius: 50%;
          background: rgba(239, 68, 68, 0.9) !important;
          color: white;
          cursor: pointer;
        ">${svgIcons.close}</button>
      `);
      cancelBtn.click(() => {
        deactivateMeasureMode();
        state.selectedTool = null;
        updateToolHighlighting(null);
        updateSelectedToolIndicator(null);
        container.find('#fab_tools').hide();
        container.find('#fab_main').css('background', 'rgba(255, 255, 255, 0.15)');
        container.find('#fab_pause_resume').hide();
        container.find('#fab_done').hide();
        container.find('#fab_axis_locks').hide();
        currentMobileMeasurement = null;
        updateMobileAxisButtons();
      });
      toolsContainer.append(cancelBtn);

      // Main FAB click handler
      container.find('#fab_main').click(function () {
        const tools = container.find('#fab_tools');
        if (tools.is(':visible')) {
          tools.hide();
        } else {
          tools.css('display', 'flex');
        }
      });

      // Pause/Resume button handler
      container.find('#fab_pause_resume').click(function () {
        if (state.measurementPaused) {
          resumeMeasurement();
        } else {
          pauseMeasurement();
        }
        updatePauseResumeButton('fab');
      });

      // Done button handler (finish measurement)
      container.find('#fab_done').click(function () {
        deactivateMeasureMode();
        state.selectedTool = null;
        updateToolHighlighting(null);
        updateSelectedToolIndicator(null);
        container.find('#fab_tools').hide();
        container.find('#fab_main').css('background', 'rgba(255, 255, 255, 0.15)');
        container.find('#fab_pause_resume').hide();
        container.find('#fab_done').hide();
        container.find('#fab_axis_locks').hide();
      });

      // Axis lock button handlers
      let currentMobileMeasurement = null;
      const updateMobileAxisButtons = () => {
        if (!currentMobileMeasurement) {
          container.find('.axis-lock-btn').removeClass('axis-locked');
          return;
        }
        container.find('#fab_axis_x').toggleClass('axis-locked', currentMobileMeasurement.lockedX);
        container.find('#fab_axis_y').toggleClass('axis-locked', currentMobileMeasurement.lockedY);
        container.find('#fab_axis_z').toggleClass('axis-locked', currentMobileMeasurement.lockedZ);
      };

      ['x', 'y', 'z'].forEach((axis) => {
        container.find(`#fab_axis_${axis}`).click(() => {
          if (!currentMobileMeasurement) return;
          const prop = `locked${axis.toUpperCase()}`;
          currentMobileMeasurement[prop] = !currentMobileMeasurement[prop];
          updateMobileAxisButtons();
        });
      });

      // Track current measurement for axis locking
      viewer.addEventListener('start_inserting_measurement', (e) => {
        if (e.measure && e.measure instanceof Measure) {
          currentMobileMeasurement = e.measure;
          updateMobileAxisButtons();
        }
      });

      viewer.addEventListener('measurement_completed', () => {
        currentMobileMeasurement = null;
        updateMobileAxisButtons();
      });

      viewer.addEventListener('cancel_insertions', () => {
        currentMobileMeasurement = null;
        updateMobileAxisButtons();
      });

      renderArea.append(container);
      return container;
    };

    // ========================================================================
    // VARIANT B: Bottom Navigation Bar
    // Horizontal bar at bottom, slides up to reveal tools
    // ========================================================================
    const createBottomUI = () => {
      const container = $(`
        <div id="mobile_ui_bottom" class="mobile-ui-container" style="
          position: absolute;
          z-index: 10000;
          bottom: 0;
          left: 0;
          right: 0;
          display: none;
          flex-direction: column;
          align-items: center;
        ">
          <!-- Pull-up handle -->
          <div id="bottom_handle" style="
            width: 100%;
            background: linear-gradient(180deg, transparent 0%, rgba(15, 23, 42, 0.95) 100%);
            padding: 8px 0 0 0;
            display: flex;
            justify-content: center;
            cursor: pointer;
          ">
            <div style="
              width: 48px;
              height: 5px;
              background: rgba(255,255,255,0.4);
              border-radius: 3px;
            "></div>
          </div>
          
          <!-- Main bar -->
          <div id="bottom_bar" style="
            width: 100%;
            background: rgba(15, 23, 42, 0.98);
            backdrop-filter: blur(12px);
            padding: 12px 16px env(safe-area-inset-bottom, 12px) 16px;
            display: flex;
            justify-content: space-around;
            align-items: center;
            gap: 8px;
            border-top: 1px solid rgba(255,255,255,0.1);
          ">
            <!-- Mode toggle -->
            <button id="bottom_mode_toggle" class="mobile-tool-btn" style="
              flex: 0 0 auto;
              padding: 10px 16px;
              border-radius: 12px;
              color: white;
              font-size: 14px;
              font-weight: 600;
              cursor: pointer;
              display: flex;
              align-items: center;
              gap: 6px;
            ">
              <span id="bottom_mode_icon">${svgIcons.compass}</span>
              <span>NAV</span>
            </button>
            
            <!-- Tools scroll container -->
            <div id="bottom_tools_scroll" style="
              flex: 1;
              overflow-x: auto;
              display: flex;
              gap: 8px;
              padding: 4px;
              -webkit-overflow-scrolling: touch;
              scrollbar-width: none;
            "></div>
            
            <!-- Pause/Resume button (shown when measurement is active) -->
            <button id="bottom_pause_resume" class="mobile-tool-btn" style="
              flex: 0 0 auto;
              width: 44px;
              height: 44px;
              border-radius: 12px;
              color: white;
              cursor: pointer;
              display: none;
              align-items: center;
              justify-content: center;
            ">${svgIcons.pause}</button>
          </div>
          
          <!-- Expanded panel (slides up) -->
          <div id="bottom_expanded" style="
            position: absolute;
            bottom: 100%;
            left: 0;
            right: 0;
            background: rgba(15, 23, 42, 0.98);
            backdrop-filter: blur(12px);
            padding: 16px;
            display: none;
            flex-direction: column;
            gap: 12px;
            border-top: 1px solid rgba(255,255,255,0.1);
            animation: slideUp 0.3s ease-out;
            max-height: 50vh;
            overflow-y: auto;
          ">
            <div style="color: white; font-weight: 600; text-align: center; opacity: 0.7;">Select Measurement Tool</div>
            <div id="bottom_tools_grid" style="
              display: grid;
              grid-template-columns: repeat(4, 1fr);
              gap: 12px;
            "></div>
          </div>
        </div>
      `);

      // Build inline tool buttons (compact)
      const scrollContainer = container.find('#bottom_tools_scroll');
      const gridContainer = container.find('#bottom_tools_grid');

      measureTools.forEach((tool) => {
        // Compact inline button
        const inlineBtn = $(`
          <button class="mobile-tool-btn bottom-tool-inline" data-tool="${tool.name}" style="
            width: 44px;
            height: 44px;
            flex-shrink: 0;
            border-radius: 12px;
            cursor: pointer;
            display: flex;
            align-items: center;
            justify-content: center;
          ">
            <img src="${Potree.resourcePath}/icons/${tool.icon}" style="width: 24px; height: 24px; filter: brightness(0) invert(1);" />
          </button>
        `);
        inlineBtn.click(() => {
          handleToolClick(tool);
          container.find('#bottom_mode_toggle').css({
            'background': 'rgba(34, 197, 94, 0.9)'
          });
          container.find('#bottom_mode_icon').html(svgIcons.rulerSmall);
          container.find('#bottom_mode_toggle span:last').text('MEAS');
          updatePauseResumeButton('bottom');
        });
        scrollContainer.append(inlineBtn);

        // Grid button (in expanded panel)
        const gridBtn = $(`
          <button class="mobile-tool-btn bottom-tool-grid" data-tool="${tool.name}" style="
            aspect-ratio: 1;
            border-radius: 12px;
            cursor: pointer;
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            gap: 6px;
            padding: 12px;
          ">
            <img src="${Potree.resourcePath}/icons/${tool.icon}" style="width: 24px; height: 24px; filter: brightness(0) invert(1);" />
            <span style="color: white; font-size: 11px; font-weight: 500;" data-i18n="${tool.labelKey}">${getToolLabel(tool)}</span>
          </button>
        `);
        gridBtn.click(() => {
          handleToolClick(tool);
          container.find('#bottom_expanded').hide();
          container.find('#bottom_mode_toggle').css({
            'background': 'rgba(34, 197, 94, 0.9)'
          });
          container.find('#bottom_mode_icon').html(svgIcons.rulerSmall);
          container.find('#bottom_mode_toggle span:last').text('MEAS');
          updatePauseResumeButton('bottom');
        });
        gridContainer.append(gridBtn);
      });

      // Handle expand
      container.find('#bottom_handle').click(() => {
        const expanded = container.find('#bottom_expanded');
        expanded.toggle();
      });

      // Mode toggle
      container.find('#bottom_mode_toggle').click(function () {
        if (state.measureModeActive) {
          deactivateMeasureMode();
          state.selectedTool = null;
          updateToolHighlighting(null);
          updateSelectedToolIndicator(null);
          $(this).css({
            'background': 'rgba(255, 255, 255, 0.15)'
          });
          container.find('#bottom_mode_icon').html(svgIcons.compass);
          $(this).find('span:last').text('NAV');
          container.find('#bottom_pause_resume').hide();
        } else {
          // Just show expanded panel to select tool
          container.find('#bottom_expanded').toggle();
        }
      });

      // Pause/Resume button handler
      container.find('#bottom_pause_resume').click(function () {
        if (state.measurementPaused) {
          resumeMeasurement();
        } else {
          pauseMeasurement();
        }
        updatePauseResumeButton('bottom');
      });

      renderArea.append(container);
      return container;
    };

    // ========================================================================
    // VARIANT C: Floating Mini Button
    // Draggable small button that expands into a menu
    // ========================================================================
    const createMinimalUI = () => {
      const container = $(`
        <div id="mobile_ui_minimal" class="mobile-ui-container" style="
          position: absolute;
          z-index: 10000;
          display: none;
        ">
          <!-- Floating wrapper - this moves when dragged -->
          <div id="floating_wrapper" style="
            position: fixed;
            bottom: 20px;
            left: 20px;
            z-index: 10001;
          ">
            <!-- Container for menu button and measurement controls -->
            <div style="
              display: flex;
              align-items: center;
              gap: 8px;
              position: relative;
              overflow: visible;
            ">
              <!-- The actual button -->
              <div id="floating_btn" style="
                width: 44px;
                height: 44px;
                border-radius: 50%;
                background: rgba(255, 255, 255, 0.15);
                backdrop-filter: blur(8px);
                border: 1px solid rgba(255, 255, 255, 0.3);
                cursor: move;
                display: flex;
                align-items: center;
                justify-content: center;
                color: white;
                box-shadow: 0 4px 16px rgba(0, 0, 0, 0.3);
                transition: transform 0.2s ease, background 0.2s ease, box-shadow 0.3s ease;
                touch-action: none;
                user-select: none;
                position: relative;
                flex-shrink: 0;
                overflow: visible;
              ">
                <span id="floating_btn_icon">${svgIcons.ruler}</span>
              </div>
              
              <!-- Pause/Resume button (shown next to button during measurement) -->
              <button id="floating_pause_resume" class="mobile-tool-btn" style="
                width: 44px;
                height: 44px;
                border-radius: 50%;
                color: white;
                cursor: pointer;
                display: none;
                align-items: center;
                justify-content: center;
                flex-shrink: 0;
              ">${svgIcons.pause}</button>
              
              <!-- Done button (shown next to button during measurement) -->
              <button id="floating_done" class="mobile-tool-btn" style="
                width: 44px;
                height: 44px;
                border-radius: 50%;
                background: rgba(34, 197, 94, 0.9) !important;
                color: white;
                cursor: pointer;
                display: none;
                align-items: center;
                justify-content: center;
                flex-shrink: 0;
              ">${svgIcons.check}</button>
            </div>
            
            <!-- Expanded menu - positioned relative to wrapper (above button) -->
            <div id="floating_menu" style="
              position: absolute;
              bottom: 54px;
              right: 0;
              background: rgba(20, 20, 25, 0.95);
              backdrop-filter: blur(16px);
              border-radius: 24px;
              border: 1px solid rgba(255, 255, 255, 0.1);
              padding: 16px;
              display: none;
              flex-direction: column;
              gap: 12px;
              box-shadow: 0 8px 32px rgba(0, 0, 0, 0.5);
              transform-origin: bottom right;
              animation: scaleIn 0.2s ease-out;
              z-index: 10001;
            ">
            <!-- Selected tool indicator in menu header -->
            <div id="floating_menu_selected_tool" style="
              display: none;
              align-items: center;
              justify-content: center;
              gap: 8px;
              padding: 8px 12px;
              background: rgba(34, 197, 94, 0.15);
              border-radius: 12px;
              border: 1px solid rgba(34, 197, 94, 0.3);
            ">
              <img id="floating_menu_selected_tool_img" style="width: 20px; height: 20px; filter: brightness(0) invert(1);" />
              <span id="floating_menu_selected_tool_label" style="color: rgba(255, 255, 255, 0.9); font-size: 11px; font-weight: 600;"></span>
            </div>
            <div id="floating_tools" style="
              display: grid;
              grid-template-columns: repeat(4, 1fr);
              gap: 8px;
            "></div>
            <div style="display: flex; gap: 8px; justify-content: flex-end; align-items: center;">
              <button id="floating_close" class="mobile-tool-btn" style="
                padding: 8px 16px;
                border-radius: 16px;
                color: rgba(255, 255, 255, 0.9);
                font-size: 12px;
                cursor: pointer;
              " data-i18n="mobile.close"></button>
            </div>
          </div>
          </div>
        </div>
      `);

      // Build tools in menu
      const toolsContainer = container.find('#floating_tools');
      measureTools.forEach((tool) => {
        const btn = $(`
          <button class="mobile-tool-btn floating-tool" data-tool="${tool.name}" style="
            width: 52px;
            height: 52px;
            border-radius: 14px;
            background: rgba(255, 255, 255, 0.08);
            border: 1px solid rgba(255, 255, 255, 0.12);
            cursor: pointer;
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            gap: 4px;
            transition: all 0.15s ease;
          ">
            <img src="${Potree.resourcePath}/icons/${tool.icon}" style="width: 22px; height: 22px; filter: brightness(0) invert(1); opacity: 0.85;" />
            <span style="color: rgba(255,255,255,0.6); font-size: 8px; font-weight: 500;" data-i18n="${tool.labelKey}">${getToolLabel(tool)}</span>
          </button>
        `);
        btn.click(() => {
          handleToolClick(tool);
          container.find('#floating_menu').hide();
          container.find('#floating_btn').css('background', 'rgba(34, 197, 94, 0.6)');
          container.find('#floating_btn_icon').html(svgIcons.ruler);
          container.find('#floating_done').css('display', 'flex');
          // Show tool indicator and pause button next to menu icon
          updatePauseResumeButton('minimal');
        });
        toolsContainer.append(btn);
      });

      const floatingWrapper = container.find('#floating_wrapper');
      const floatingBtn = container.find('#floating_btn');
      const floatingMenu = container.find('#floating_menu');

      // Show drag hint message after 2 seconds if user hasn't interacted yet
      let dragHintShown = false;
      let hasDragged = false;
      const dragHintTimeout = setTimeout(() => {
        if (!hasDragged && !dragHintShown) {
          const i18nInstance = (typeof i18n !== 'undefined' ? i18n : window.i18n);
          const message = i18nInstance ? i18nInstance.t('mobile.drag_hint') : 'Drag to move';
          viewer.postMessage(message, { duration: 5000 });
          dragHintShown = true;
        }
      }, 2000);

      // Function to update menu position based on available space
      const updateMenuPosition = () => {
        if (!floatingMenu.is(':visible')) return;

        const wrapperRect = floatingWrapper[0].getBoundingClientRect();
        const menuWidth = 260;
        const menuHeight = 250;
        const gap = 10;
        const buttonSize = 44;

        // Determine vertical position
        const spaceAbove = wrapperRect.top;
        const spaceBelow = window.innerHeight - wrapperRect.bottom;
        const showBelow = spaceAbove < menuHeight + gap && spaceBelow > spaceAbove;

        // Determine horizontal position
        const spaceLeft = wrapperRect.left;
        const spaceRight = window.innerWidth - wrapperRect.right;
        const extendRight = spaceLeft < menuWidth && spaceRight > spaceLeft;

        const menuCss = {};

        // Vertical
        if (showBelow) {
          menuCss.top = (buttonSize + gap) + 'px';
          menuCss.bottom = 'auto';
        } else {
          menuCss.bottom = (buttonSize + gap) + 'px';
          menuCss.top = 'auto';
        }

        // Horizontal
        if (extendRight) {
          menuCss.left = '0';
          menuCss.right = 'auto';
        } else {
          menuCss.right = '0';
          menuCss.left = 'auto';
        }

        // Transform origin for animation
        const originY = showBelow ? 'top' : 'bottom';
        const originX = extendRight ? 'left' : 'right';
        menuCss.transformOrigin = `${originY} ${originX}`;

        floatingMenu.css(menuCss);
      };

      // Dragging logic - now dragging the wrapper
      let isDragging = false;
      let hasMoved = false;
      let startX, startY, startLeft, startTop;

      const onDragStart = (e) => {
        isDragging = true;
        hasMoved = false;
        const touch = e.touches ? e.touches[0] : e;
        startX = touch.clientX;
        startY = touch.clientY;
        const rect = floatingWrapper[0].getBoundingClientRect();
        startLeft = rect.left;
        startTop = rect.top;
        floatingWrapper.css('transition', 'none');
      };

      const onDragMove = (e) => {
        if (!isDragging) return;
        const touch = e.touches ? e.touches[0] : e;
        const deltaX = touch.clientX - startX;
        const deltaY = touch.clientY - startY;

        if (Math.abs(deltaX) > 5 || Math.abs(deltaY) > 5) {
          hasMoved = true;
          // Mark as dragged to prevent showing drag hint
          if (!hasDragged) {
            hasDragged = true;
            clearTimeout(dragHintTimeout);
          }
        }

        const newLeft = Math.max(10, Math.min(window.innerWidth - 54, startLeft + deltaX));
        const newTop = Math.max(10, Math.min(window.innerHeight - 54, startTop + deltaY));

        // Move the wrapper
        floatingWrapper.css({
          left: newLeft + 'px',
          top: newTop + 'px',
          right: 'auto',
          bottom: 'auto'
        });

        // Update menu position dynamically while dragging
        updateMenuPosition();
      };

      const onDragEnd = () => {
        isDragging = false;
        floatingWrapper.css('transition', 'none');
      };

      floatingBtn[0].addEventListener('touchstart', onDragStart, { passive: true });
      floatingBtn[0].addEventListener('mousedown', onDragStart);
      document.addEventListener('touchmove', onDragMove, { passive: true });
      document.addEventListener('mousemove', onDragMove);
      document.addEventListener('touchend', onDragEnd);
      document.addEventListener('mouseup', onDragEnd);
      viewer.onDispose(() => {
        document.removeEventListener('touchmove', onDragMove);
        document.removeEventListener('mousemove', onDragMove);
        document.removeEventListener('touchend', onDragEnd);
        document.removeEventListener('mouseup', onDragEnd);
      });

      // Tap to open menu (only if not dragged)
      floatingBtn.on('click touchend', (e) => {
        if (hasMoved) {
          hasMoved = false;
          return;
        }
        e.preventDefault();

        // Mark as interacted on first click
        if (!hasDragged && dragHintShown) {
          hasDragged = true;
        }

        if (floatingMenu.is(':visible')) {
          floatingMenu.hide();
        } else {
          // Show menu and position it
          floatingMenu.css('display', 'flex');
          updateMenuPosition();
        }
      });

      // Close button
      container.find('#floating_close').click(() => {
        floatingMenu.hide();
      });

      // Done button (finish measurement)
      container.find('#floating_done').click(() => {
        deactivateMeasureMode();
        state.selectedTool = null;
        updateToolHighlighting(null);
        updateSelectedToolIndicator(null);
        floatingBtn.css('background', 'rgba(255, 255, 255, 0.15)');
        container.find('#floating_done').hide();
        container.find('#floating_pause_resume').hide();
        floatingMenu.hide();
      });

      // Pause/Resume button handler
      container.find('#floating_pause_resume').click(function () {
        if (state.measurementPaused) {
          resumeMeasurement();
        } else {
          pauseMeasurement();
        }
        updatePauseResumeButton('minimal');
      });

      renderArea.append(container);
      return container;
    };

    // ========================================================================
    // Initialize all UI variants
    // ========================================================================
    // Load saved language from localStorage (will be used after i18n init)
    let savedLanguage = null;
    try {
      savedLanguage = localStorage.getItem('potree_mobile_language');
      // Valid language codes: en, pl, fr, de, jp, es, se, zh, it, ca
      const validLanguages = ['en', 'pl', 'fr', 'de', 'jp', 'es', 'se', 'zh', 'it', 'ca'];
      if (savedLanguage && !validLanguages.includes(savedLanguage)) {
        console.warn('[localStorage] Invalid language value, clearing:', savedLanguage);
        localStorage.removeItem('potree_mobile_language');
        savedLanguage = null;
      }
    } catch (e) {
      console.warn('[localStorage] Failed to load language:', e);
      savedLanguage = null;
    }

    i18nInstance.init(
      {
        lng: savedLanguage || 'en', // Use saved language or default to 'en'
        resGetPath: Potree.resourcePath + '/lang/__lng__/__ns__.json',
        preload: ['en', 'pl'],
        getAsync: true,
        debug: false,
      },
      () => {
        // Same early-out as the sidebar path: the i18n init is async, so a
        // cancelled/disposed viewer must not append its toolbar and modals
        // into the render area the replacement viewer now owns.
        if (isStale()) return;

        const fabUI = createFabUI();
        const bottomUI = createBottomUI();
        const minimalUI = createMinimalUI();

        // Apply i18n translations to mobile UI elements
        fabUI.i18n();
        bottomUI.i18n();
        minimalUI.i18n();

        // If saved language exists, set it now and re-apply translations
        if (savedLanguage) {
          i18nInstance.setLng(savedLanguage, () => {
            fabUI.i18n();
            bottomUI.i18n();
            minimalUI.i18n();
            $('body').i18n();
          });
        }

        const variants = {
          '1': { el: minimalUI, name: 'minimal', desc: 'Minimal' },
          '2': { el: bottomUI, name: 'bottom', desc: 'Bottom' },
          '3': { el: fabUI, name: 'fab', desc: 'FAB' }
        };


        // ========================================================================
        // SETTINGS MENU - Top-left corner
        // ========================================================================
        let onMobileLanguageChanged = null;
        const createSettingsMenu = () => {
          // Settings icon button - Liquid Glass Effect (iOS 26 style)
          const settingsIcon = $(`
            <button id="mobile_settings_icon" class="mobile-tool-btn" style="
              position: fixed;
              top: 12px;
              left: 12px;
              width: 48px;
              height: 48px;
              border-radius: 50%;
              background: linear-gradient(135deg, rgba(255,255,255,0.25) 0%, rgba(255,255,255,0.08) 50%, rgba(255,255,255,0.15) 100%);
              backdrop-filter: blur(20px) saturate(180%);
              -webkit-backdrop-filter: blur(20px) saturate(180%);
              border: 1px solid rgba(255,255,255,0.3);
              box-shadow: 0 4px 24px rgba(0,0,0,0.15), inset 0 1px 1px rgba(255,255,255,0.4), inset 0 -1px 2px rgba(0,0,0,0.1);
              color: white;
              font-size: 20px;
              cursor: pointer;
              display: flex;
              align-items: center;
              justify-content: center;
              z-index: 10002;
              transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
            ">${svgIcons.settings}</button>
          `);

          // Settings modal/dialog
          const settingsModal = $(`
            <div id="mobile_settings_modal" style="
              position: fixed;
              top: 0;
              left: 0;
              right: 0;
              bottom: 0;
              background: rgba(0, 0, 0, 0.7);
              backdrop-filter: blur(8px);
              -webkit-backdrop-filter: blur(8px);
              z-index: 10003;
              display: none;
              align-items: center;
              justify-content: center;
              padding: 20px;
              font-family: -apple-system, BlinkMacSystemFont, 'SF Pro Display', 'SF Pro Text', 'Inter', system-ui, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
              -webkit-font-smoothing: antialiased;
              -moz-osx-font-smoothing: grayscale;
              letter-spacing: -0.01em;
            ">
              <div style="
                background: linear-gradient(145deg, rgba(40,40,45,0.92) 0%, rgba(25,25,30,0.95) 100%);
                backdrop-filter: blur(24px) saturate(180%);
                -webkit-backdrop-filter: blur(24px) saturate(180%);
                border-radius: 20px;
                border: 1px solid rgba(255, 255, 255, 0.15);
                max-width: 400px;
                width: 100%;
                max-height: 90vh;
                overflow-y: auto;
                box-shadow: 0 8px 40px rgba(0,0,0,0.4), inset 0 1px 1px rgba(255,255,255,0.1), 0 0 0 1px rgba(0,0,0,0.3);
                padding: 24px;
              ">
                <div style="
                  display: flex;
                  justify-content: space-between;
                  align-items: center;
                  margin-bottom: 24px;
                  padding-bottom: 16px;
                  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
                ">
                  <h3 style="
                    color: white;
                    font-size: 20px;
                    font-weight: 600;
                    margin: 0;
                  " data-i18n="mobile.settings">Settings</h3>
                  <button id="mobile_settings_close" class="mobile-tool-btn" style="
                    width: 32px;
                    height: 32px;
                    border-radius: 50%;
                    background: rgba(255, 255, 255, 0.1);
                    border: none;
                    color: white;
                    font-size: 18px;
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                  ">${svgIcons.close}</button>
                </div>

                <!-- Menu Layout Selection -->
                <div style="margin-bottom: 24px;">
                  <label style="
                    display: block;
                    color: rgba(255, 255, 255, 0.9);
                    font-size: 14px;
                    font-weight: 500;
                    margin-bottom: 12px;
                  " data-i18n="mobile.menu_layout">Menu Layout</label>
                  <div id="mobile_settings_layout_buttons" style="
                    display: flex;
                    gap: 8px;
                    flex-wrap: wrap;
                  "></div>
                </div>

                <!-- Language Selection -->
                <div style="margin-bottom: 24px;">
                  <label style="
                    display: block;
                    color: rgba(255, 255, 255, 0.9);
                    font-size: 14px;
                    font-weight: 500;
                    margin-bottom: 12px;
                  " data-i18n="appearance.language">Language</label>
                  <div id="mobile_settings_language_buttons" style="
                    display: flex;
                    gap: 8px;
                    flex-wrap: wrap;
                  "></div>
                </div>

                <!-- Point Budget -->
                <div style="margin-bottom: 24px;">
                  <label style="
                    display: block;
                    color: rgba(255, 255, 255, 0.9);
                    font-size: 14px;
                    font-weight: 500;
                    margin-bottom: 12px;
                  " data-i18n="mobile.point_budget">Point Budget</label>
                  <div style="
                    display: flex;
                    align-items: center;
                    gap: 12px;
                  ">
                    <div id="mobile_settings_point_budget_slider" style="
                      flex: 1;
                      height: 6px;
                      background: rgba(255, 255, 255, 0.1);
                      border-radius: 3px;
                      position: relative;
                      cursor: pointer;
                      margin: 0 8px;
                    ">
                      <div id="mobile_settings_point_budget_fill" style="
                        position: absolute;
                        top: 0;
                        left: 0;
                        height: 100%;
                        background: linear-gradient(90deg, #667eea 0%, #764ba2 100%);
                        border-radius: 3px;
                        width: 0%;
                        transition: width 0.2s ease;
                      "></div>
                      <div id="mobile_settings_point_budget_handle" style="
                        position: absolute;
                        top: 50%;
                        left: 0%;
                        transform: translate(-50%, -50%);
                        width: 20px;
                        height: 20px;
                        background: white;
                        border-radius: 50%;
                        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
                        cursor: grab;
                        transition: left 0.2s ease;
                        z-index: 1;
                      "></div>
                    </div>
                    <span id="mobile_settings_point_budget_value" style="
                      color: white;
                      font-size: 14px;
                      font-weight: 500;
                      min-width: 80px;
                      text-align: right;
                    ">100K</span>
                  </div>
                </div>
              </div>
            </div>
          `);

          // Add layout buttons
          const layoutOptions = [
            { key: 'minimal', value: '1', labelKey: 'mobile.layout_minimal' },
            { key: 'bottom', value: '2', labelKey: 'mobile.layout_bottom' },
            { key: 'fab', value: '3', labelKey: 'mobile.layout_fab' }
          ];

          const layoutContainer = settingsModal.find('#mobile_settings_layout_buttons');
          layoutOptions.forEach((layout) => {
            const btn = $(`
              <button class="mobile-settings-option-btn" data-layout="${layout.value}" style="
                flex: 1;
                min-width: 100px;
                padding: 12px 16px;
                border-radius: 12px;
                background: linear-gradient(135deg, rgba(255,255,255,0.15) 0%, rgba(255,255,255,0.05) 50%, rgba(255,255,255,0.1) 100%);
                backdrop-filter: blur(12px) saturate(150%);
                -webkit-backdrop-filter: blur(12px) saturate(150%);
                border: 1px solid rgba(255, 255, 255, 0.25);
                box-shadow: 0 2px 12px rgba(0,0,0,0.1), inset 0 1px 1px rgba(255,255,255,0.3);
                color: white;
                font-size: 14px;
                font-weight: 500;
                cursor: pointer;
                transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
              " data-i18n="${layout.labelKey}">${i18nInstance.t(layout.labelKey) || layout.key}</button>
            `);
            btn.click(() => {
              // Update active state - Liquid Glass style
              layoutContainer.find('.mobile-settings-option-btn').css({
                'background': 'linear-gradient(135deg, rgba(255,255,255,0.15) 0%, rgba(255,255,255,0.05) 50%, rgba(255,255,255,0.1) 100%)',
                'border-color': 'rgba(255, 255, 255, 0.25)',
                'box-shadow': '0 2px 12px rgba(0,0,0,0.1), inset 0 1px 1px rgba(255,255,255,0.3)'
              });
              btn.css({
                'background': 'linear-gradient(135deg, rgba(102,126,234,0.4) 0%, rgba(102,126,234,0.2) 50%, rgba(102,126,234,0.3) 100%)',
                'border-color': 'rgba(102, 126, 234, 0.7)',
                'box-shadow': '0 4px 20px rgba(102,126,234,0.3), inset 0 1px 1px rgba(255,255,255,0.4), 0 0 15px rgba(102,126,234,0.2)'
              });
              // Switch UI variant
              if (window.switchMobileUI) {
                const variantMap = { '1': 'minimal', '2': 'bottom', '3': 'fab' };
                window.switchMobileUI(variantMap[layout.value]);
              }
            });
            layoutContainer.append(btn);
          });

          // Add language buttons
          const languages = [
            ['EN', 'en'],
            ['PL', 'pl'],
          ];

          const languageContainer = settingsModal.find('#mobile_settings_language_buttons');
          languages.forEach(([key, value]) => {
            const btn = $(`
              <button class="mobile-settings-option-btn" data-lang="${value}" style="
                padding: 10px 16px;
                border-radius: 12px;
                background: linear-gradient(135deg, rgba(255,255,255,0.15) 0%, rgba(255,255,255,0.05) 50%, rgba(255,255,255,0.1) 100%);
                backdrop-filter: blur(12px) saturate(150%);
                -webkit-backdrop-filter: blur(12px) saturate(150%);
                border: 1px solid rgba(255, 255, 255, 0.25);
                box-shadow: 0 2px 12px rgba(0,0,0,0.1), inset 0 1px 1px rgba(255,255,255,0.3);
                color: white;
                font-size: 14px;
                font-weight: 500;
                cursor: pointer;
                transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
              ">${key}</button>
            `);
            btn.click(() => {
              // Update active state - Liquid Glass style
              languageContainer.find('.mobile-settings-option-btn').css({
                'background': 'linear-gradient(135deg, rgba(255,255,255,0.15) 0%, rgba(255,255,255,0.05) 50%, rgba(255,255,255,0.1) 100%)',
                'border-color': 'rgba(255, 255, 255, 0.25)',
                'box-shadow': '0 2px 12px rgba(0,0,0,0.1), inset 0 1px 1px rgba(255,255,255,0.3)'
              });
              btn.css({
                'background': 'linear-gradient(135deg, rgba(102,126,234,0.4) 0%, rgba(102,126,234,0.2) 50%, rgba(102,126,234,0.3) 100%)',
                'border-color': 'rgba(102, 126, 234, 0.7)',
                'box-shadow': '0 4px 20px rgba(102,126,234,0.3), inset 0 1px 1px rgba(255,255,255,0.4), 0 0 15px rgba(102,126,234,0.2)'
              });
              // Change language - use callback to update mobile UI elements immediately
              viewer.setLanguage(value, () => {
                // Update all mobile UI elements after language is loaded
                fabUI.i18n();
                bottomUI.i18n();
                minimalUI.i18n();
                if (typeof onMobileLanguageChanged === 'function') {
                  onMobileLanguageChanged();
                }
              });
            });
            languageContainer.append(btn);
          });

          // Point budget slider
          const slider = settingsModal.find('#mobile_settings_point_budget_slider');
          const fill = settingsModal.find('#mobile_settings_point_budget_fill');
          const handle = settingsModal.find('#mobile_settings_point_budget_handle');
          const valueDisplay = settingsModal.find('#mobile_settings_point_budget_value');

          const updateSlider = (value) => {
            const min = 10000;
            const max = 10000000;
            const percentage = ((value - min) / (max - min)) * 100;
            fill.css('width', percentage + '%');
            handle.css('left', percentage + '%');

            // Format value
            let formatted;
            if (value >= 1000000) {
              formatted = (value / 1000000).toFixed(1) + 'M';
            } else if (value >= 1000) {
              formatted = (value / 1000).toFixed(0) + 'K';
            } else {
              formatted = value.toString();
            }
            valueDisplay.text(formatted);
          };

          // Initialize with current budget
          const currentBudget = viewer.getPointBudget();
          updateSlider(currentBudget);

          // Make slider interactive
          let isDragging = false;
          const handleSliderMove = (clientX) => {
            const rect = slider[0].getBoundingClientRect();
            const x = clientX - rect.left;
            const percentage = Math.max(0, Math.min(100, (x / rect.width) * 100));
            const min = 10000;
            const max = 10000000;
            const value = Math.round(min + (percentage / 100) * (max - min));
            // Round to nearest 1000
            const roundedValue = Math.round(value / 1000) * 1000;
            viewer.setPointBudget(roundedValue);
            updateSlider(roundedValue);
          };

          slider.on('mousedown touchstart', (e) => {
            isDragging = true;
            const clientX = e.type === 'mousedown' ? e.clientX : e.touches[0].clientX;
            handleSliderMove(clientX);
            e.preventDefault();
          });

          const onSliderDragMove = (e) => {
            if (isDragging) {
              const clientX = e.type === 'mousemove' ? e.clientX : e.touches[0].clientX;
              handleSliderMove(clientX);
              e.preventDefault();
            }
          };
          const onSliderDragEnd = () => {
            isDragging = false;
          };
          $(document).on('mousemove touchmove', onSliderDragMove);
          $(document).on('mouseup touchend', onSliderDragEnd);
          viewer.onDispose(() => {
            $(document).off('mousemove touchmove', onSliderDragMove);
            $(document).off('mouseup touchend', onSliderDragEnd);
          });

          // Listen for budget changes from elsewhere
          viewer.addEventListener('point_budget_changed', () => {
            updateSlider(viewer.getPointBudget());
          });

          // Open/close handlers
          settingsIcon.click(() => {
            settingsModal.css('display', 'flex');
            // Apply translations
            settingsModal.i18n();
            // Update active states
            const currentVariant = currentUIVariant;
            const variantMap = { 'minimal': '1', 'bottom': '2', 'fab': '3' };
            const currentVariantKey = variantMap[currentVariant] || '3';
            layoutContainer.find('.mobile-settings-option-btn').css({
              'background': 'linear-gradient(135deg, rgba(255,255,255,0.15) 0%, rgba(255,255,255,0.05) 50%, rgba(255,255,255,0.1) 100%)',
              'border-color': 'rgba(255, 255, 255, 0.25)',
              'box-shadow': '0 2px 12px rgba(0,0,0,0.1), inset 0 1px 1px rgba(255,255,255,0.3)'
            });
            layoutContainer.find(`[data-layout="${currentVariantKey}"]`).css({
              'background': 'linear-gradient(135deg, rgba(102,126,234,0.4) 0%, rgba(102,126,234,0.2) 50%, rgba(102,126,234,0.3) 100%)',
              'border-color': 'rgba(102, 126, 234, 0.7)',
              'box-shadow': '0 4px 20px rgba(102,126,234,0.3), inset 0 1px 1px rgba(255,255,255,0.4), 0 0 15px rgba(102,126,234,0.2)'
            });

            // Update language active state
            let currentLang = 'en';
            if (i18nInstance) {
              currentLang = typeof i18nInstance.lng === 'function' ? i18nInstance.lng() : (i18nInstance.language || 'en');
            } else if (typeof i18n !== 'undefined') {
              currentLang = typeof i18n.lng === 'function' ? i18n.lng() : (i18n.language || 'en');
            }
            languageContainer.find('.mobile-settings-option-btn').css({
              'background': 'linear-gradient(135deg, rgba(255,255,255,0.15) 0%, rgba(255,255,255,0.05) 50%, rgba(255,255,255,0.1) 100%)',
              'border-color': 'rgba(255, 255, 255, 0.25)',
              'box-shadow': '0 2px 12px rgba(0,0,0,0.1), inset 0 1px 1px rgba(255,255,255,0.3)'
            });
            languageContainer.find(`[data-lang="${currentLang}"]`).css({
              'background': 'linear-gradient(135deg, rgba(102,126,234,0.4) 0%, rgba(102,126,234,0.2) 50%, rgba(102,126,234,0.3) 100%)',
              'border-color': 'rgba(102, 126, 234, 0.7)',
              'box-shadow': '0 4px 20px rgba(102,126,234,0.3), inset 0 1px 1px rgba(255,255,255,0.4), 0 0 15px rgba(102,126,234,0.2)'
            });
          });

          // Function to save all settings to localStorage when closing the menu
          const saveSettingsToLocalStorage = () => {
            try {
              const pointBudget = viewer.getPointBudget();
              // i18n.lng is a function, need to call it or use .language property
              let language = 'en';
              if (i18nInstance) {
                language = typeof i18nInstance.lng === 'function' ? i18nInstance.lng() : (i18nInstance.language || 'en');
              } else if (typeof i18n !== 'undefined') {
                language = typeof i18n.lng === 'function' ? i18n.lng() : (i18n.language || 'en');
              }
              const uiVariant = currentUIVariant;

              localStorage.setItem('potree_mobile_point_budget', pointBudget.toString());
              localStorage.setItem('potree_mobile_language', language);
              localStorage.setItem('potree_mobile_ui_variant', uiVariant);

            } catch (e) {
              console.warn('[localStorage] Failed to save settings:', e);
            }
          };

          settingsModal.find('#mobile_settings_close').click(() => {
            saveSettingsToLocalStorage();
            settingsModal.hide();
          });

          settingsModal.click((e) => {
            if (e.target.id === 'mobile_settings_modal') {
              saveSettingsToLocalStorage();
              settingsModal.hide();
            }
          });

          renderArea.append(settingsIcon);
          $('body').append(settingsModal);
          viewer.onDispose(() => settingsModal.remove());

          return { icon: settingsIcon, modal: settingsModal };
        };

        // ========================================================================
        // MEASUREMENTS LIST - Top-left, below settings
        // ========================================================================
        const createMeasurementsMenu = () => {
          const formatMeasurementInfo = (item) => {
            if (!(item instanceof Measure) || item.points.length < 2) return '';
            try {
              if (item.showArea) return `${item.getArea().toFixed(2)} m²`;
              if (item.showDistances && !item.showAngles && !item.showHeight) {
                return `${item.getTotalDistance().toFixed(2)} m`;
              }
            } catch (e) {
              /* ignore format errors */
            }
            return '';
          };

          const listIcon = $(`
            <button id="mobile_measurements_icon" class="mobile-tool-btn" style="
              position: fixed;
              top: 68px;
              left: 12px;
              width: 48px;
              height: 48px;
              border-radius: 50%;
              background: linear-gradient(135deg, rgba(255,255,255,0.25) 0%, rgba(255,255,255,0.08) 50%, rgba(255,255,255,0.15) 100%);
              backdrop-filter: blur(20px) saturate(180%);
              -webkit-backdrop-filter: blur(20px) saturate(180%);
              border: 1px solid rgba(255,255,255,0.3);
              box-shadow: 0 4px 24px rgba(0,0,0,0.15), inset 0 1px 1px rgba(255,255,255,0.4), inset 0 -1px 2px rgba(0,0,0,0.1);
              color: white;
              font-size: 20px;
              cursor: pointer;
              display: flex;
              align-items: center;
              justify-content: center;
              z-index: 10002;
              transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
            " data-i18n="[title]mobile.measurements" title="Measurements">${svgIcons.list}</button>
          `);

          const measurementsModal = $(`
            <div id="mobile_measurements_modal" style="
              position: fixed;
              top: 0;
              left: 0;
              right: 0;
              bottom: 0;
              background: rgba(0, 0, 0, 0.7);
              backdrop-filter: blur(8px);
              -webkit-backdrop-filter: blur(8px);
              z-index: 10003;
              display: none;
              align-items: center;
              justify-content: center;
              padding: 20px;
              font-family: -apple-system, BlinkMacSystemFont, 'SF Pro Display', 'SF Pro Text', 'Inter', system-ui, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
              -webkit-font-smoothing: antialiased;
              -moz-osx-font-smoothing: grayscale;
              letter-spacing: -0.01em;
            ">
              <div style="
                background: linear-gradient(145deg, rgba(40,40,45,0.92) 0%, rgba(25,25,30,0.95) 100%);
                backdrop-filter: blur(24px) saturate(180%);
                -webkit-backdrop-filter: blur(24px) saturate(180%);
                border-radius: 20px;
                border: 1px solid rgba(255, 255, 255, 0.15);
                max-width: 400px;
                width: 100%;
                max-height: 90vh;
                overflow: hidden;
                display: flex;
                flex-direction: column;
                box-shadow: 0 8px 40px rgba(0,0,0,0.4), inset 0 1px 1px rgba(255,255,255,0.1), 0 0 0 1px rgba(0,0,0,0.3);
                padding: 24px;
              ">
                <div style="
                  display: flex;
                  justify-content: space-between;
                  align-items: center;
                  margin-bottom: 16px;
                  padding-bottom: 16px;
                  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
                  flex-shrink: 0;
                ">
                  <h3 style="
                    color: white;
                    font-size: 20px;
                    font-weight: 600;
                    margin: 0;
                  " data-i18n="mobile.measurements">Measurements</h3>
                  <button id="mobile_measurements_close" class="mobile-tool-btn" style="
                    width: 32px;
                    height: 32px;
                    border-radius: 50%;
                    background: rgba(255, 255, 255, 0.1);
                    border: none;
                    color: white;
                    font-size: 18px;
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                  ">${svgIcons.close}</button>
                </div>

                <div id="mobile_measurements_list" style="
                  flex: 1;
                  overflow-y: auto;
                  -webkit-overflow-scrolling: touch;
                  min-height: 80px;
                  max-height: 50vh;
                "></div>

                <div id="mobile_measurements_footer" style="
                  flex-shrink: 0;
                  margin-top: 16px;
                  padding-top: 16px;
                  border-top: 1px solid rgba(255, 255, 255, 0.1);
                  display: none;
                ">
                  <button id="mobile_measurements_remove_all" class="mobile-tool-btn" style="
                    width: 100%;
                    padding: 14px 16px;
                    border-radius: 12px;
                    background: linear-gradient(135deg, rgba(239,68,68,0.35) 0%, rgba(239,68,68,0.15) 50%, rgba(239,68,68,0.25) 100%);
                    backdrop-filter: blur(12px) saturate(150%);
                    -webkit-backdrop-filter: blur(12px) saturate(150%);
                    border: 1px solid rgba(239, 68, 68, 0.45);
                    box-shadow: 0 2px 12px rgba(0,0,0,0.1), inset 0 1px 1px rgba(255,255,255,0.15);
                    color: white;
                    font-size: 14px;
                    font-weight: 600;
                    cursor: pointer;
                    touch-action: manipulation;
                  " data-i18n="mobile.remove_all_measurements">Remove all</button>
                </div>
              </div>
            </div>
          `);

          const listEl = measurementsModal.find('#mobile_measurements_list');
          const footerEl = measurementsModal.find('#mobile_measurements_footer');

          const renderMeasurementsList = () => {
            const items = viewer.scene ? [...viewer.scene.measurements] : [];
            listEl.empty();

            if (items.length === 0) {
              footerEl.hide();
              listEl.append(`
                <div style="
                  color: rgba(255, 255, 255, 0.55);
                  font-size: 14px;
                  text-align: center;
                  padding: 32px 12px;
                " data-i18n="mobile.measurements_empty">${i18nInstance.t('mobile.measurements_empty') || 'No measurements yet'}</div>
              `);
              listEl.i18n();
              return;
            }

            footerEl.show();
            items.forEach((item) => {
              const name = item.name || '(unnamed)';
              const info = formatMeasurementInfo(item);
              const row = $(`
                <div class="mobile-measurement-row" style="
                  display: flex;
                  align-items: center;
                  gap: 10px;
                  min-height: 48px;
                  padding: 8px 10px;
                  margin-bottom: 8px;
                  border-radius: 12px;
                  background: linear-gradient(135deg, rgba(255,255,255,0.12) 0%, rgba(255,255,255,0.04) 50%, rgba(255,255,255,0.08) 100%);
                  border: 1px solid rgba(255, 255, 255, 0.15);
                  box-shadow: inset 0 1px 1px rgba(255,255,255,0.2);
                ">
                  <div style="flex: 1; min-width: 0;">
                    <div class="mobile-measurement-name" style="
                      color: white;
                      font-size: 14px;
                      font-weight: 500;
                      overflow: hidden;
                      text-overflow: ellipsis;
                      white-space: nowrap;
                    "></div>
                    <div class="mobile-measurement-info" style="
                      color: rgba(255, 255, 255, 0.55);
                      font-size: 12px;
                      margin-top: 2px;
                      display: ${info ? 'block' : 'none'};
                    "></div>
                  </div>
                  <button class="mobile-tool-btn mobile-measurement-delete" type="button" style="
                    flex-shrink: 0;
                    width: 44px;
                    height: 44px;
                    border-radius: 12px;
                    background: rgba(239, 68, 68, 0.2);
                    border: 1px solid rgba(239, 68, 68, 0.35);
                    color: #fca5a5;
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    touch-action: manipulation;
                  " data-i18n="[title]mobile.remove_measurement" title="Remove measurement">${svgIcons.trash}</button>
                </div>
              `);
              row.find('.mobile-measurement-name').text(name);
              if (info) row.find('.mobile-measurement-info').text(info);
              row.find('.mobile-measurement-delete').on('click', (e) => {
                e.stopPropagation();
                viewer.scene.removeMeasurement(item);
              });
              listEl.append(row);
            });
            listEl.i18n();
            footerEl.i18n();
          };

          listIcon.click(() => {
            renderMeasurementsList();
            measurementsModal.css('display', 'flex').i18n();
          });

          measurementsModal.find('#mobile_measurements_close').click(() => {
            measurementsModal.hide();
          });

          measurementsModal.click((e) => {
            if (e.target.id === 'mobile_measurements_modal') {
              measurementsModal.hide();
            }
          });

          measurementsModal.find('#mobile_measurements_remove_all').click(() => {
            if (viewer.scene) viewer.scene.removeAllMeasurements();
          });

          const onMeasurementsChanged = () => {
            if (measurementsModal.css('display') !== 'none') {
              renderMeasurementsList();
            }
          };

          if (viewer.scene) {
            viewer.scene.addEventListener('measurement_added', onMeasurementsChanged);
            viewer.scene.addEventListener('measurement_removed', onMeasurementsChanged);
          }

          renderArea.append(listIcon);
          $('body').append(measurementsModal);
          viewer.onDispose(() => measurementsModal.remove());

          return {
            icon: listIcon,
            modal: measurementsModal,
            refresh: renderMeasurementsList,
          };
        };

        createSettingsMenu();
        const measurementsMenu = createMeasurementsMenu();

        // Called from settings language buttons after i18n reload
        onMobileLanguageChanged = () => {
          measurementsMenu.modal.i18n();
          measurementsMenu.icon.i18n();
          if (measurementsMenu.modal.css('display') !== 'none') {
            measurementsMenu.refresh();
          }
        };

        // Switch function
        const switchToVariant = (variantKey) => {
          // Hide all
          Object.values(variants).forEach(v => v.el.hide());

          // Show selected
          variants[variantKey].el.show();
          currentUIVariant = variants[variantKey].name;

        };

        // Show saved variant or default to FAB (1)
        const variantKeyMap = { 'minimal': '1', 'bottom': '2', 'fab': '3' };
        const defaultVariantKey = variantKeyMap[savedUIVariant] || '3';
        switchToVariant(defaultVariantKey);

        // Listen for measurement completion to hide pause/resume button
        const checkMeasurementState = () => {
          const currentVariant = currentUIVariant;
          if (currentVariant === 'fab' || currentVariant === 'bottom' || currentVariant === 'minimal') {
            updatePauseResumeButton(currentVariant);
          }
        };

        // Check state periodically and on events
        const stateCheckInterval = setInterval(() => {
          if (!state.measureModeActive && state.measurementPaused) {
            // Measurement was deactivated while paused - reset state
            state.measurementPaused = false;
            state.pausedMeasurement = null;
            checkMeasurementState();
          } else if (state.measureModeActive) {
            checkMeasurementState();
          }
        }, 200);
        viewer.onDispose(() => clearInterval(stateCheckInterval));

        // Also check on cancel_insertions event
        viewer.addEventListener('cancel_insertions', () => {
          // Don't reset paused state if we're intentionally paused
          if (!state.measurementPaused) {
            // Measurement was cancelled - check if we should hide button
            setTimeout(checkMeasurementState, 100);
          }
        });

        // Listen for measurement_completed event (fired when maxMarkers reached)
        viewer.addEventListener('measurement_completed', (e) => {

          // Reset state
          state.measureModeActive = false;
          state.measurementPaused = false;
          state.pausedMeasurement = null;
          state.selectedTool = null;

          // Reset tool highlighting
          updateToolHighlighting(null);
          updateSelectedToolIndicator(null);

          // Hide done and pause buttons for all variants
          $('#fab_done, #fab_pause_resume').hide();
          $('#bottom_done, #bottom_pause_resume').hide();
          $('#floating_done, #floating_pause_resume').hide();

          // Reset button colors to default (not green/active)
          $('#fab_main').css('background', 'rgba(255, 255, 255, 0.15)');
          $('#floating_btn').css('background', 'rgba(255, 255, 255, 0.15)');
          $('#bottom_mode_toggle').css({ 'background': 'rgba(255, 255, 255, 0.15)' });
          $('#bottom_mode_icon').html(svgIcons.compass);
          $('#bottom_mode_toggle span:last').text('NAV');

          // Update button appearance
          checkMeasurementState();
        });

        // Listen for annotation_completed event
        viewer.addEventListener('annotation_completed', (e) => {

          // Reset state
          state.measureModeActive = false;
          state.measurementPaused = false;
          state.pausedMeasurement = null;
          state.selectedTool = null;

          // Reset tool highlighting
          updateToolHighlighting(null);
          updateSelectedToolIndicator(null);

          // Hide done and pause buttons for all variants
          $('#fab_done, #fab_pause_resume').hide();
          $('#bottom_done, #bottom_pause_resume').hide();
          $('#floating_done, #floating_pause_resume').hide();

          // Reset button colors to default (not green/active)
          $('#fab_main').css('background', 'rgba(255, 255, 255, 0.15)');
          $('#floating_btn').css('background', 'rgba(255, 255, 255, 0.15)');
          $('#bottom_mode_toggle').css({ 'background': 'rgba(255, 255, 255, 0.15)' });
          $('#bottom_mode_icon').html(svgIcons.compass);
          $('#bottom_mode_toggle span:last').text('NAV');

          // Update button appearance
          checkMeasurementState();
        });

        // Console switch function (still available)
        window.switchMobileUI = (variant) => {
          const keyMap = { 'minimal': '1', 'bottom': '2', 'fab': '3' };
          if (keyMap[variant]) {
            switchToVariant(keyMap[variant]);
            checkMeasurementState();
          }
        };

        // Hide sidebar container on mobile
        $('#potree_sidebar_container').hide();

        // Style description for mobile - small and in bottom right corner
        // Adjust position to avoid collision with right toolbar
        $('#potree_description').css({
          'top': 'auto',
          'bottom': '10px',
          'right': '10px',
          'left': 'auto',
          'transform': 'none',
          'font-size': '0.7em',
          'max-width': 'calc(50% - 20px)', // Account for right toolbar space
          'text-align': 'right',
          'pointer-events': 'none' // Don't interfere with touches
        });

        // Style images inside description to fit container width on mobile
        $('#potree_description img').css({
          'max-width': '100%',
          'width': '100%',
          'height': 'auto',
          'display': 'block',
          'object-fit': 'scale-down'
        });

        // Also add CSS rule for dynamically added images
        if (!$('#mobile_description_img_style').length) {
          $('<style id="mobile_description_img_style">')
            .prop('type', 'text/css')
            .html('#potree_description img { max-width: 100% !important; width: 100% !important; height: auto !important; display: block !important; object-fit: scale-down !important; }')
            .appendTo('head');
        }

        // Don't create menu toggle button on mobile
        // (toolbar is always visible)

        // Initialize map view if needed
        this.mapView = new MapView(this);
        this.mapView.init();

        // Mark GUI as loaded. The host callback passed to loadGUI() is one of
        // the queued tasks — do not invoke it separately here.
        $(() => {
          if (isStale()) return;
          markGuiLoaded(this);
        });
      }
    );
  }

  setLanguage(lang, callback) {
    // setLng is async when getAsync: true, so we need to use callback
    i18n.setLng(lang, () => {
      // Update all translations after language is loaded
      $('body').i18n();
      // Titles composed from i18n strings in JS (not data-i18n) re-render on this.
      this.dispatchEvent({ type: 'language_changed', language: lang });
      // Call optional callback if provided
      if (callback) {
        callback();
      }
    });

  }

  setServer(server) {
    this.server = server;
  }

  initDragAndDrop() {
    function allowDrag(e) {
      e.dataTransfer.dropEffect = 'copy';
      e.preventDefault();
    }

    let dropHandler = async (event) => {
      event.preventDefault();

      for (const item of event.dataTransfer.items) {
        if (item.kind !== 'file') {
          continue;
        }

        const file = item.getAsFile();

        const isJson5 = file.name.toLowerCase().endsWith('.json5');
        const isGeoPackage = file.name.toLowerCase().endsWith('.gpkg');

        if (isJson5) {
          try {
            const text = await file.text();
            const json = JSON5.parse(text);

            if (json.type === 'Potree') {
              Potree.loadProject(viewer, json);
            }
          } catch (e) {
            console.error('failed to parse the dropped file as JSON');
            console.error(e);
          }
        } else if (isGeoPackage) {
          const hasPointcloud = viewer.scene.pointclouds.length > 0;

          if (!hasPointcloud) {
            let msg = 'At least one point cloud is needed that specifies the ';
            msg += 'coordinate reference system before loading vector data.';
            console.error(msg);
          } else {
            proj4.defs(
              'WGS84',
              '+proj=longlat +ellps=WGS84 +datum=WGS84 +no_defs'
            );
            proj4.defs('pointcloud', this.getProjection());
            let transform = proj4('WGS84', 'pointcloud');

            const buffer = await file.arrayBuffer();

            const params = {
              transform: transform,
              source: file.name,
            };

            const geo = await Potree.GeoPackageLoader.loadBuffer(
              buffer,
              params
            );
            viewer.scene.addGeopackage(geo);
          }
        }
      }
    };

    $('body')[0].addEventListener('dragenter', allowDrag);
    $('body')[0].addEventListener('dragover', allowDrag);
    $('body')[0].addEventListener('drop', dropHandler);
  }

  initThree() {

    let width = this.renderArea.clientWidth;
    let height = this.renderArea.clientHeight;

    let contextAttributes = {
      alpha: true,
      depth: true,
      stencil: false,
      antialias: false,
      //premultipliedAlpha: _premultipliedAlpha,
      preserveDrawingBuffer: true,
      powerPreference: 'high-performance',
    };

    // let contextAttributes = {
    // 	alpha: false,
    // 	preserveDrawingBuffer: true,
    // };

    // let contextAttributes = {
    // 	alpha: false,
    // 	preserveDrawingBuffer: true,
    // };

    let canvas = document.createElement('canvas');

    // 1. Sprawdzenie WebGL 2 — probe the real canvas rather than a throwaway
    // one: every extra context counts toward the browser's per-page limit
    // until it is garbage-collected.
    let context = null;
    try {
      context = canvas.getContext('webgl2', contextAttributes);
    } catch { context = null; }

    if (!context) {
      this.renderArea.innerHTML = `
        <div style="display:flex;height:100%;align-items:center;justify-content:center;
                    font-family:sans-serif;text-align:center;padding:2em">
            <div>
                <h2>WebGL 2 required</h2>
                <p>Twoja przeglądarka lub urządzenie nie obsługuje WebGL 2.<br>
                   Spróbuj na nowszym systemie / sprzęcie.</p><br>
                <p>Your browser or device does not support WebGL 2.<br>
                    Please try on a newer system / device.</p>
            </div>
        </div>`;
      throw new Error('WebGL 2 not available - aborting Viewer initialisation.');
    }

    // 2. Tworzymy już prawdziwe płótno w trybie webgl2
    this.renderer = new THREE.WebGLRenderer({
      alpha: true,
      premultipliedAlpha: false,
      canvas: canvas,
      context: context,
    });
    this.renderer.sortObjects = false;
    this.renderer.setSize(width, height);
    this.renderer.autoClear = false;
    this.renderArea.appendChild(this.renderer.domElement);
    this.renderer.domElement.tabIndex = '2222';
    this.renderer.domElement.style.position = 'absolute';
    this.renderer.domElement.addEventListener('mousedown', () => {
      this.renderer.domElement.focus();
    });
    //this.renderer.domElement.focus();

    // NOTE: If extension errors occur, pass the string into this.renderer.extensions.get(x) before enabling
    // enable frag_depth extension for the interpolation shader, if available
    let gl = this.renderer.getContext();
    gl.getExtension('EXT_frag_depth');
    gl.getExtension('WEBGL_depth_texture');
    gl.getExtension('WEBGL_color_buffer_float'); // Enable explicitly for more portability, EXT_color_buffer_float is the proper name in WebGL 2

    if (gl.createVertexArray == null) {
      let extVAO = gl.getExtension('OES_vertex_array_object');

      if (!extVAO) {
        throw new Error('OES_vertex_array_object extension not supported');
      }

      gl.createVertexArray = extVAO.createVertexArrayOES.bind(extVAO);
      gl.bindVertexArray = extVAO.bindVertexArrayOES.bind(extVAO);
    }
  }

  updateAnnotations() {
    if (!this.visibleAnnotations) {
      this.visibleAnnotations = new Set();
    }

    this.scene.annotations.updateBounds();
    this.scene.cameraP.updateMatrixWorld();
    this.scene.cameraO.updateMatrixWorld();

    let distances = [];

    let renderAreaSize = this.renderer.getSize(new THREE.Vector2());

    let viewer = this;

    let visibleNow = [];
    this.scene.annotations.traverse((annotation) => {
      if (annotation === this.scene.annotations) {
        return true;
      }

      if (!annotation.visible) {
        return false;
      }

      if (annotation.viewer === null) {
        annotation.viewer = this;
        annotation.installHandles(this);
      }

      annotation.scene = this.scene;

      let element = annotation.domElement;

      let position = annotation.position.clone();
      position.add(annotation.offset);
      if (!position) {
        position = annotation.boundingBox.getCenter(new THREE.Vector3());
      }

      let distance = viewer.scene.cameraP.position.distanceTo(position);
      let radius = annotation.boundingBox.getBoundingSphere(
        new THREE.Sphere()
      ).radius;

      let screenPos = new THREE.Vector3();
      let screenSize = 0;

      {
        // SCREEN POS
        screenPos.copy(position).project(this.scene.getActiveCamera());
        screenPos.x = (renderAreaSize.x * (screenPos.x + 1)) / 2;
        screenPos.y = renderAreaSize.y * (1 - (screenPos.y + 1) / 2);

        // SCREEN SIZE
        if (viewer.scene.cameraMode == CameraMode.PERSPECTIVE) {
          let fov = (Math.PI * viewer.scene.cameraP.fov) / 180;
          let slope = Math.tan(fov / 2.0);
          let projFactor = (0.5 * renderAreaSize.y) / (slope * distance);
          screenSize = radius * projFactor;
        } else {
          screenSize = Utils.projectedRadiusOrtho(
            radius,
            viewer.scene.cameraO.projectionMatrix,
            renderAreaSize.x,
            renderAreaSize.y
          );
        }
      }

      element.css('left', screenPos.x + 'px');
      element.css('top', screenPos.y + 'px');
      //element.css("display", "block");

      let zIndex = 10000000 - distance * (10000000 / this.scene.cameraP.far);
      if (annotation.descriptionVisible) {
        zIndex += 10000000;
      }
      element.css('z-index', parseInt(zIndex));

      if (annotation.children.length > 0) {
        let expand =
          screenSize > annotation.collapseThreshold ||
          annotation.boundingBox.containsPoint(
            this.scene.getActiveCamera().position
          );
        annotation.expand = expand;

        if (!expand) {
          //annotation.display = (screenPos.z >= -1 && screenPos.z <= 1);
          let inFrustum = screenPos.z >= -1 && screenPos.z <= 1;
          if (inFrustum) {
            visibleNow.push(annotation);
          }
        }

        return expand;
      } else {
        //annotation.display = (screenPos.z >= -1 && screenPos.z <= 1);
        let inFrustum = screenPos.z >= -1 && screenPos.z <= 1;
        if (inFrustum) {
          visibleNow.push(annotation);
        }
      }
    });

    let notVisibleAnymore = new Set(this.visibleAnnotations);
    for (let annotation of visibleNow) {
      annotation.display = true;

      notVisibleAnymore.delete(annotation);
    }
    this.visibleAnnotations = visibleNow;

    for (let annotation of notVisibleAnymore) {
      annotation.display = false;
    }
  }

  updateMaterialDefaults(pointcloud) {
    // PROBLEM STATEMENT:
    // * [min, max] of intensity, source id, etc. are computed as point clouds are loaded
    // * the point cloud material won't know the range it should use until some data is loaded
    // * users can modify the range at runtime, but sensible default ranges should be
    //   applied even if no GUI is present
    // * display ranges shouldn't suddenly change even if the actual range changes over time.
    //   e.g. the root node has intensity range [1, 478]. One of the descendants increases range to
    //   [0, 2047]. We should not automatically change to the new range because that would result
    //   in sudden and drastic changes of brightness. We should adjust the min/max of the sidebar slider.

    const material = pointcloud.material;

    const attIntensity = pointcloud.getAttribute('intensity');

    if (attIntensity != null && material.intensityRange[0] === Infinity) {
      material.intensityRange = [...attIntensity.range];
    }

    // const attIntensity = pointcloud.getAttribute("intensity");
    // if(attIntensity && material.intensityRange[0] === Infinity){
    // 	material.intensityRange = [...attIntensity.range];
    // }

    // let attributes = pointcloud.getAttributes();

    // for(let attribute of attributes.attributes){
    // 	if(attribute.range){
    // 		let range = [...attribute.range];
    // 		material.computedRange.set(attribute.name, range);
    // 		//material.setRange(attribute.name, range);
    // 	}
    // }
  }

  update(delta, timestamp) {
    if (Potree.measureTimings) performance.mark('update-start');

    this.dispatchEvent({
      type: 'update_start',
      delta: delta,
      timestamp: timestamp,
    });

    const scene = this.scene;
    const camera = scene.getActiveCamera();
    const visiblePointClouds = this.scene.pointclouds.filter(
      (pc) => pc.visible
    );

    // Cache limit for loaded points. The floor matters: with a small point
    // budget, budget*2 leaves so little headroom that LRU eviction (which
    // disposes a node's loaded DESCENDANTS along with it — see LRU.freeMemory)
    // destroys freshly loaded nodes before they are ever displayed. They get
    // re-requested next frame, evicted again, and the loader churns octree.bin
    // downloads forever. The floor gives fresh loads room to settle; for the
    // default 1M+ budgets the limit is unchanged (budget * 2).
    Potree.pointLoadLimit = Math.max(Potree.pointBudget * 2, Potree.pointBudget + 2_000_000);

    const lTarget = camera.position
      .clone()
      .add(camera.getWorldDirection(new THREE.Vector3()).multiplyScalar(1000));
    this.scene.directionalLight.position.copy(camera.position);
    this.scene.directionalLight.lookAt(lTarget);

    for (let pointcloud of visiblePointClouds) {
      pointcloud.showBoundingBox = this.showBoundingBox;
      pointcloud.generateDEM = this.generateDEM;
      pointcloud.minimumNodePixelSize = this.minNodeSize;

      let material = pointcloud.material;

      material.uniforms.uFilterReturnNumberRange.value =
        this.filterReturnNumberRange;
      material.uniforms.uFilterNumberOfReturnsRange.value =
        this.filterNumberOfReturnsRange;
      material.uniforms.uFilterGPSTimeClipRange.value = this.filterGPSTimeRange;
      material.uniforms.uFilterPointSourceIDClipRange.value =
        this.filterPointSourceIDRange;

      material.classification = this.classifications;
      material.recomputeClassification();

      this.updateMaterialDefaults(pointcloud);
    }

    {
      if (this.showBoundingBox) {
        let bbRoot = this.scene.scene.getObjectByName(
          'potree_bounding_box_root'
        );
        if (!bbRoot) {
          let node = new THREE.Object3D();
          node.name = 'potree_bounding_box_root';
          this.scene.scene.add(node);
          bbRoot = node;
        }

        let visibleBoxes = [];
        for (let pointcloud of this.scene.pointclouds) {
          for (let node of pointcloud.visibleNodes.filter(
            (vn) => vn.boundingBoxNode !== undefined
          )) {
            let box = node.boundingBoxNode;
            visibleBoxes.push(box);
          }
        }

        bbRoot.children = visibleBoxes;
      }
    }

    if (!this.freeze) {
      let result = Potree.updatePointClouds(
        scene.pointclouds,
        camera,
        this.renderer
      );

      // DEBUG - ONLY DISPLAY NODES THAT INTERSECT MOUSE
      //if(false){

      //	let renderer = viewer.renderer;
      //	let mouse = viewer.inputHandler.mouse;

      //	let nmouse = {
      //		x: (mouse.x / renderer.domElement.clientWidth) * 2 - 1,
      //		y: -(mouse.y / renderer.domElement.clientHeight) * 2 + 1
      //	};

      //	let pickParams = {};

      //	//if(params.pickClipped){
      //	//	pickParams.pickClipped = params.pickClipped;
      //	//}

      //	pickParams.x = mouse.x;
      //	pickParams.y = renderer.domElement.clientHeight - mouse.y;

      //	let raycaster = new THREE.Raycaster();
      //	raycaster.setFromCamera(nmouse, camera);
      //	let ray = raycaster.ray;

      //	for(let pointcloud of scene.pointclouds){
      //		let nodes = pointcloud.nodesOnRay(pointcloud.visibleNodes, ray);
      //		pointcloud.visibleNodes = nodes;

      //	}
      //}

      // const tStart = performance.now();
      // const worldPos = new THREE.Vector3();
      // const camPos = viewer.scene.getActiveCamera().getWorldPosition(new THREE.Vector3());
      // let lowestDistance = Infinity;
      // let numNodes = 0;

      // viewer.scene.scene.traverse(node => {
      // 	node.getWorldPosition(worldPos);

      // 	const distance = worldPos.distanceTo(camPos);

      // 	lowestDistance = Math.min(lowestDistance, distance);

      // 	numNodes++;

      // 	if(Number.isNaN(distance)){
      // 		console.error(":(");
      // 	}
      // });
      // const duration = (performance.now() - tStart).toFixed(2);

      // Potree.debug.computeNearDuration = duration;
      // Potree.debug.numNodes = numNodes;

      const tStart = performance.now();
      const campos = camera.position;
      let closestImage = Infinity;
      for (const images of this.scene.orientedImages) {
        for (const image of images.images) {
          const distance = image.mesh.position.distanceTo(campos);

          closestImage = Math.min(closestImage, distance);
        }
      }
      const tEnd = performance.now();

      if (result.lowestSpacing !== Infinity) {
        let near = result.lowestSpacing * 10.0;
        let far = -this.getBoundingBox().applyMatrix4(camera.matrixWorldInverse)
          .min.z;

        far = Math.max(far * 1.5, 10000);
        near = Math.min(100.0, Math.max(0.01, near));
        near = Math.min(near, closestImage);
        far = Math.max(far, near + 10000);

        if (near === Infinity) {
          near = 0.1;
        }

        camera.near = near;
        camera.far = far;
      } else {
        // don't change near and far in this case
      }

    }

    this.scene.cameraP.fov = this.fov;

    let controls = this.getControls();
    if (controls === this.deviceControls) {
      this.controls.setScene(scene);
      this.controls.update(delta);

      this.scene.cameraP.position.copy(scene.view.position);
      this.scene.cameraO.position.copy(scene.view.position);
      if (this.scene.cameraMode === CameraMode.ORTHOGRAPHIC) {
        fitOrthographicDepthToBox(
          this.scene.cameraO,
          this.scene.getBoundingBox(),
        );
      }
    } else if (controls !== null) {
      controls.setScene(scene);
      controls.update(delta);

      if (typeof debugDisabled === 'undefined') {
        this.scene.cameraP.position.copy(scene.view.position);
        this.scene.cameraP.rotation.order = 'ZXY';
        this.scene.cameraP.rotation.x = Math.PI / 2 + this.scene.view.pitch;
        this.scene.cameraP.rotation.z = this.scene.view.yaw;
      }

      this.scene.cameraO.position.copy(scene.view.position);
      this.scene.cameraO.rotation.order = 'ZXY';
      this.scene.cameraO.rotation.x = Math.PI / 2 + this.scene.view.pitch;
      this.scene.cameraO.rotation.z = this.scene.view.yaw;
      if (this.scene.cameraMode === CameraMode.ORTHOGRAPHIC) {
        fitOrthographicDepthToBox(
          this.scene.cameraO,
          this.scene.getBoundingBox(),
        );
      }
    }

    camera.updateMatrix();
    camera.updateMatrixWorld();
    camera.matrixWorldInverse.copy(camera.matrixWorld).invert();

    {
      if (this._previousCamera === undefined) {
        this._previousCamera = this.scene.getActiveCamera().clone();
        this._previousCamera.rotation.copy(
          this.scene.getActiveCamera().rotation
        );
      }

      if (!this._previousCamera.matrixWorld.equals(camera.matrixWorld)) {
        this.dispatchEvent({
          type: 'camera_changed',
          previous: this._previousCamera,
          camera: camera,
        });
      } else if (
        !this._previousCamera.projectionMatrix.equals(camera.projectionMatrix)
      ) {
        this.dispatchEvent({
          type: 'camera_changed',
          previous: this._previousCamera,
          camera: camera,
        });
      }

      this._previousCamera = this.scene.getActiveCamera().clone();
      this._previousCamera.rotation.copy(this.scene.getActiveCamera().rotation);
    }

    {
      // update clip boxes
      let boxes = [];

      // volumes with clipping enabled
      //boxes.push(...this.scene.volumes.filter(v => (v.clip)));
      boxes.push(
        ...this.scene.volumes.filter((v) => v.clip && v instanceof BoxVolume)
      );

      // profile segments
      for (let profile of this.scene.profiles) {
        boxes.push(...profile.boxes);
      }

      // Needed for .getInverse(), pre-empt a determinant of 0, see #815 / #816
      let degenerate = (box) => box.matrixWorld.determinant() !== 0;

      let clipBoxes = boxes.filter(degenerate).map((box) => {
        box.updateMatrixWorld();

        let boxInverse = box.matrixWorld.clone().invert();
        let boxPosition = box.getWorldPosition(new THREE.Vector3());

        return { box: box, inverse: boxInverse, position: boxPosition };
      });

      let clipPolygons = this.scene.polygonClipVolumes.filter(
        (vol) => vol.initialized
      );

      // set clip volumes in material
      for (let pointcloud of visiblePointClouds) {
        pointcloud.material.setClipBoxes(clipBoxes);
        pointcloud.material.setClipPolygons(
          clipPolygons,
          this.clippingTool.maxPolygonVertices
        );
        pointcloud.material.clipTask = this.clipTask;
        pointcloud.material.clipMethod = this.clipMethod;
      }

      {
        const selected = this.scene.selectedSurface;
        const byCloud = new Map();
        const visible = (this.scene.surfaces || []).filter(
          (s) => s.visible && s.occCount > 0
        );
        visible.sort((a, b) => {
          if (a === selected) return 1;
          if (b === selected) return -1;
          return a.createdAt - b.createdAt;
        });
        for (const s of visible) {
          const pc = s.pointcloud;
          if (!pc) continue;
          if (!byCloud.has(pc)) byCloud.set(pc, []);
          const list = byCloud.get(pc);
          if (list.length < SURFACE_GPU_CAP) list.push(s);
        }
        const snap = 1;
        for (let pointcloud of visiblePointClouds) {
          pointcloud.material.setSurfaces(byCloud.get(pointcloud) || [], pointcloud);
          if (pointcloud.material.uniforms.uSurfaceSnap) {
            pointcloud.material.uniforms.uSurfaceSnap.value = snap;
          }
        }
      }

      // Reality meshes (3D Tiles): same BoxVolumes / ClipTask / ClipMethod (#700)
      const realityMeshes = this.scene.realityMeshes;
      if (realityMeshes && realityMeshes.length > 0) {
        if (!this.realityMeshClipController) {
          this.realityMeshClipController = createRealityMeshClipController();
        }
        this.realityMeshClipController.sync(this, realityMeshes);
      }
    }

    {
      for (let pointcloud of visiblePointClouds) {
        pointcloud.material.elevationGradientRepeat =
          this.elevationGradientRepeat;
      }
    }

    {
      // update navigation cube
      this.navigationCube.update(camera.rotation);
    }

    this.updateAnnotations();

    if (this.mapView) {
      this.mapView.update(delta);
      if (this.mapView.sceneProjection) {
        $('#potree_map_toggle').css('display', 'block');
      }
    }

    TWEEN.update(timestamp);

    this.dispatchEvent({
      type: 'update',
      delta: delta,
      timestamp: timestamp,
    });

    if (Potree.measureTimings) {
      performance.mark('update-end');
      performance.measure('update', 'update-start', 'update-end');
    }
  }

  getPRenderer() {
    if (this.useHQ) {
      if (!this.hqRenderer) {
        this.hqRenderer = new HQSplatRenderer(this);
      }
      this.hqRenderer.useEDL = this.useEDL;

      return this.hqRenderer;
    } else {
      if (this.useEDL && !this._edlRendererFailed && this.isEDLSupported()) {
        try {
          if (!this.edlRenderer) {
            this.edlRenderer = new EDLRenderer(this);
          }
          return this.edlRenderer;
        } catch (e) {
          this.disableEDLAfterFailure(e);
        }
      }

      if (!this.potreeRenderer) {
        this.potreeRenderer = new PotreeRenderer(this);
      }
      return this.potreeRenderer;
    }
  }

  renderVR() {
    let renderer = this.renderer;

    renderer.setClearColor(0x550000, 0);
    renderer.clear();

    let xr = renderer.xr;
    let dbg = new THREE.PerspectiveCamera();
    let xrCameras = xr.getCamera(dbg);

    if (xrCameras.cameras.length !== 2) {
      return;
    }

    let makeCam = this.vrControls.getCamera.bind(this.vrControls);

    {
      // clear framebuffer
      if (viewer.background === 'skybox') {
        renderer.setClearColor(0xff0000, 1);
      } else if (viewer.background === 'gradient') {
        renderer.setClearColor(0x112233, 1);
      } else if (viewer.background === 'black') {
        renderer.setClearColor(0x000000, 1);
      } else if (viewer.background === 'white') {
        renderer.setClearColor(0xffffff, 1);
      } else {
        renderer.setClearColor(0x000000, 0);
      }

      renderer.clear();
    }

    // render background
    if (this.background === 'skybox') {
      let { skybox } = this;

      let cam = makeCam();
      skybox.camera.rotation.copy(cam.rotation);
      skybox.camera.fov = cam.fov;
      skybox.camera.aspect = cam.aspect;

      // let dbg = new THREE.Object3D();
      let dbg = skybox.parent;
      // dbg.up.set(0, 0, 1);
      dbg.rotation.x = Math.PI / 2;

      // skybox.camera.parent = dbg;
      // dbg.children.push(skybox.camera);

      dbg.updateMatrix();
      dbg.updateMatrixWorld();

      skybox.camera.updateMatrix();
      skybox.camera.updateMatrixWorld();
      skybox.camera.updateProjectionMatrix();

      renderer.render(skybox.scene, skybox.camera);
      // renderer.render(skybox.scene, cam);
    } else if (this.background === 'gradient') {
      // renderer.render(this.scene.sceneBG, this.scene.cameraBG);
    }

    this.renderer.xr.getSession().updateRenderState({
      depthNear: 0.1,
      depthFar: 10000,
    });

    let cam = null;
    let view = null;

    {
      // render world scene
      cam = makeCam();
      cam.position.z -= 0.8 * cam.scale.x;
      cam.parent = null;
      // cam.near = 0.05;
      cam.near = viewer.scene.getActiveCamera().near;
      cam.far = viewer.scene.getActiveCamera().far;
      cam.updateMatrix();
      cam.updateMatrixWorld();

      this.scene.scene.updateMatrix();
      this.scene.scene.updateMatrixWorld();
      this.scene.scene.matrixAutoUpdate = false;

      let camWorld = cam.matrixWorld.clone();
      view = camWorld.clone().invert();
      this.scene.scene.matrix.copy(view);
      this.scene.scene.matrixWorld.copy(view);

      cam.matrix.identity();
      cam.matrixWorld.identity();
      cam.matrixWorldInverse.identity();

      renderer.render(this.scene.scene, cam);

      this.scene.scene.matrixWorld.identity();
    }

    for (let pointcloud of this.scene.pointclouds) {
      let viewport = xrCameras.cameras[0].viewport;

      pointcloud.material.useEDL = false;
      pointcloud.screenHeight = viewport.height;
      pointcloud.screenWidth = viewport.width;

      // automatically switch to paraboloids because they cause far less flickering in VR,
      // when point sizes are larger than around 2 pixels
      // if(Features.SHADER_INTERPOLATION.isSupported()){
      // 	pointcloud.material.shape = Potree.PointShape.PARABOLOID;
      // }
    }

    // render point clouds
    for (let xrCamera of xrCameras.cameras) {
      let v = xrCamera.viewport;
      renderer.setViewport(v.x, v.y, v.width, v.height);

      // xrCamera.fov = 90;

      {
        // estimate VR fov
        let proj = xrCamera.projectionMatrix;
        let inv = proj.clone().invert();

        let p1 = new THREE.Vector4(0, 1, -1, 1).applyMatrix4(inv);
        let rad = p1.y;
        let fov = 180 * (rad / Math.PI);

        xrCamera.fov = fov;
      }

      for (let pointcloud of this.scene.pointclouds) {
        const { material } = pointcloud;
        material.useEDL = false;
      }

      let vrWorld = view.clone().invert();
      vrWorld.multiply(xrCamera.matrixWorld);
      let vrView = vrWorld.clone().invert();

      this.pRenderer.render(this.scene.scenePointCloud, xrCamera, null, {
        viewOverride: vrView,
      });
    }

    {
      // render VR scene
      let cam = makeCam();
      cam.parent = null;

      renderer.render(this.sceneVR, cam);
    }

    renderer.resetState();
  }

  renderDefault() {
    let pRenderer = this.getPRenderer();

    {
      // resize
      const width = this.scaleFactor * this.renderArea.clientWidth;
      const height = this.scaleFactor * this.renderArea.clientHeight;

      this.renderer.setSize(width, height);
      const pixelRatio = this.renderer.getPixelRatio();
      const aspect = width / height;

      const scene = this.scene;

      scene.cameraP.aspect = aspect;
      scene.cameraP.updateProjectionMatrix();

      let frustumScale = this.scene.view.radius;
      scene.cameraO.left = -frustumScale;
      scene.cameraO.right = frustumScale;
      scene.cameraO.top = (frustumScale * 1) / aspect;
      scene.cameraO.bottom = (-frustumScale * 1) / aspect;
      scene.cameraO.updateProjectionMatrix();

      scene.cameraScreenSpace.top = 1 / aspect;
      scene.cameraScreenSpace.bottom = -1 / aspect;
      scene.cameraScreenSpace.updateProjectionMatrix();
    }

    pRenderer.clear();

    pRenderer.render(this.renderer);
    this.renderer.render(this.overlay, this.overlayCamera);
  }

  render() {
    if (Potree.measureTimings) performance.mark('render-start');

    try {
      const vrActive = this.renderer.xr.isPresenting;

      if (vrActive) {
        this.renderVR();
      } else {
        this.renderDefault();
      }
    } catch (e) {
      if (this.disableEDLAfterFailure(e)) {
        try {
          this.renderDefault();
        } catch (e2) {
          this.onCrash(e2);
        }
      } else {
        this.onCrash(e);
      }
    }

    if (Potree.measureTimings) {
      performance.mark('render-end');
      performance.measure('render', 'render-start', 'render-end');
    }
  }

  resolveTimings(timestamp) {
    if (Potree.measureTimings) {
      if (!this.toggle) {
        this.toggle = timestamp;
      }
      let duration = timestamp - this.toggle;
      if (duration > 1000.0) {
        let measures = performance.getEntriesByType('measure');

        let names = new Set();
        for (let measure of measures) {
          names.add(measure.name);
        }

        let groups = new Map();
        for (let name of names) {
          groups.set(name, {
            measures: [],
            sum: 0,
            n: 0,
            min: Infinity,
            max: -Infinity,
          });
        }

        for (let measure of measures) {
          let group = groups.get(measure.name);
          group.measures.push(measure);
          group.sum += measure.duration;
          group.n++;
          group.min = Math.min(group.min, measure.duration);
          group.max = Math.max(group.max, measure.duration);
        }

        let glQueries = Potree.resolveQueries(this.renderer.getContext());
        for (let [key, value] of glQueries) {
          let group = {
            measures: value.map((v) => {
              return { duration: v };
            }),
            sum: value.reduce((a, i) => a + i, 0),
            n: value.length,
            min: Math.min(...value),
            max: Math.max(...value),
          };

          let groupname = `[tq] ${key}`;
          groups.set(groupname, group);
          names.add(groupname);
        }

        for (let [name, group] of groups) {
          group.mean = group.sum / group.n;
          group.measures.sort((a, b) => a.duration - b.duration);

          if (group.n === 1) {
            group.median = group.measures[0].duration;
          } else if (group.n > 1) {
            group.median = group.measures[parseInt(group.n / 2)].duration;
          }
        }

        let cn =
          Array.from(names).reduce((a, i) => Math.max(a, i.length), 0) + 5;
        let cmin = 10;
        let cmed = 10;
        let cmax = 10;
        let csam = 6;

        let message =
          ` ${'NAME'.padEnd(cn)} |` +
          ` ${'MIN'.padStart(cmin)} |` +
          ` ${'MEDIAN'.padStart(cmed)} |` +
          ` ${'MAX'.padStart(cmax)} |` +
          ` ${'SAMPLES'.padStart(csam)} \n`;
        message += ` ${'-'.repeat(message.length)}\n`;

        names = Array.from(names).sort();
        for (let name of names) {
          let group = groups.get(name);
          let min = group.min.toFixed(3);
          let median = group.median.toFixed(3);
          let max = group.max.toFixed(3);
          let n = group.n;

          message +=
            ` ${name.padEnd(cn)} |` +
            ` ${min.padStart(cmin)} |` +
            ` ${median.padStart(cmed)} |` +
            ` ${max.padStart(cmax)} |` +
            ` ${n.toString().padStart(csam)}\n`;
        }
        message += `\n`;

        performance.clearMarks();
        performance.clearMeasures();
        this.toggle = timestamp;
      }
    }
  }

  // -------------------------------------------------------------------------
  // Multi-view (alignment / CAD-style multi-pane) mode.
  //
  // When `multiView` is set, the viewer renders N panes into one canvas. Each
  // pane has its own camera, view and controls (see MultiView/PaneConfig.js).
  // The standard single-view update()/render() path is skipped in favour of
  // `_loopMultiView()` which does a simplified per-pane update + viewport
  // render. Tools that depend on a single active camera (annotations,
  // measurement, navigation cube, map view) are bypassed in this mode.
  // -------------------------------------------------------------------------

  setMultiViewLayout(panes) {
    this.clearMultiViewLayout();
    this.multiView = new MultiViewLayout(this, panes);
    this.multiViewInputHandler = new MultiViewInputHandler(this, this.multiView);
    this.dispatchEvent({ type: 'multiview_layout_set', panes });
  }

  clearMultiViewLayout() {
    if (this.multiViewInputHandler) {
      this.multiViewInputHandler.dispose();
      this.multiViewInputHandler = null;
    }
    if (this.multiView) {
      this.multiView.dispose();
      this.multiView = null;
      this.renderer.setScissorTest(false);
      this.dispatchEvent({ type: 'multiview_layout_cleared' });
    }
  }

  _loopMultiView(delta, timestamp) {
    const width = this.scaleFactor * this.renderArea.clientWidth;
    const height = this.scaleFactor * this.renderArea.clientHeight;
    if (width === 0 || height === 0) return;

    // Set canvas size once per frame. We deliberately bypass viewer.render()
    // (which calls setSize inside) so that the renderer.viewport / .scissor
    // we configure per pane below isn't reset mid-frame.
    this.renderer.setSize(width, height);

    if (this.multiViewInputHandler) {
      this.multiViewInputHandler.updateAll(delta);
    }

    const pRenderer = this.getPRenderer();

    for (const pane of this.multiView.panes) {
      const vp = pane.pixelViewport(width, height);
      if (vp.w === 0 || vp.h === 0) continue;

      // Drive scene.view from this pane's view.
      this.scene.view.position.copy(pane.view.position);
      this.scene.view.yaw = pane.view.yaw;
      this.scene.view.pitch = pane.view.pitch;
      this.scene.view.radius = pane.view.radius;
      this.setCameraMode(
        pane.cameraType === 'orthographic'
          ? CameraMode.ORTHOGRAPHIC
          : CameraMode.PERSPECTIVE,
      );

      // CRITICAL: pre-apply scene.view to scene.cameraO/cameraP BEFORE calling
      // update(). update() copies scene.view → camera at the end of the
      // function; without this pre-apply, updatePointClouds() and the first
      // ortho depth fit would see pane N-1's camera.
      {
        const camO = this.scene.cameraO;
        camO.position.copy(this.scene.view.position);
        camO.rotation.order = 'ZXY';
        camO.rotation.x = Math.PI / 2 + this.scene.view.pitch;
        camO.rotation.z = this.scene.view.yaw;
        camO.updateMatrix();
        camO.updateMatrixWorld(true);
        camO.matrixWorldInverse.copy(camO.matrixWorld).invert();
        if (pane.cameraType === 'orthographic') {
          fitOrthographicDepthToBox(camO, this.scene.getBoundingBox());
        }

        const camP = this.scene.cameraP;
        camP.position.copy(this.scene.view.position);
        camP.rotation.order = 'ZXY';
        camP.rotation.x = Math.PI / 2 + this.scene.view.pitch;
        camP.rotation.z = this.scene.view.yaw;
        camP.updateMatrix();
        camP.updateMatrixWorld(true);
        camP.matrixWorldInverse.copy(camP.matrixWorld).invert();
      }

      // Per-pane hook BEFORE update(): alignment hides/shows clouds per pane.
      // update() runs updatePointClouds against `pc.visible`; if this ran
      // after the previous pane's filter (e.g. Top hid locked scans), the
      // Combined pane would skip octree loads and render empty until the
      // pane was maximized (only that pane's loop, no prior filter).
      if (typeof pane.beforeRender === 'function') {
        pane.beforeRender(this);
      }

      this.update(delta, timestamp);

      // Recompute projection for the pane's aspect. Ortho also re-fits
      // near/far (and pulls cameraO out of the cloud) after update() copied
      // scene.view.position back onto cameraO.
      const paneAspect = vp.w / vp.h;
      const camO = this.scene.cameraO;
      const camP = this.scene.cameraP;
      const activeCamera = this.scene.getActiveCamera();

      // Make sure matrixWorldInverse is up to date (update() recomputes it
      // for the active camera but not the other).
      camO.updateMatrixWorld(true);
      camO.matrixWorldInverse.copy(camO.matrixWorld).invert();
      camP.updateMatrixWorld(true);
      camP.matrixWorldInverse.copy(camP.matrixWorld).invert();

      // Compute near/far from scene bbox in CURRENT camera view space.
      const worldBB = this.scene.getBoundingBox();
      camP.aspect = paneAspect;
      camP.updateProjectionMatrix();
      const frustumScale = this.scene.view.radius;
      camO.left = -frustumScale;
      camO.right = frustumScale;
      camO.top = frustumScale / paneAspect;
      camO.bottom = -frustumScale / paneAspect;

      if (pane.cameraType === 'orthographic') {
        // Zoom must not fly cameraO into the cloud — that near-clips a slab.
        fitOrthographicDepthToBox(camO, worldBB);
      } else {
        const viewBB = Utils.computeTransformedBoundingBox(
          worldBB,
          activeCamera.matrixWorldInverse,
        );
        const margin = 2.0;
        const nfNear = Math.max(0.1, -viewBB.max.z - margin);
        const nfFar = Math.max(nfNear + 10.0, -viewBB.min.z + margin);
        activeCamera.near = nfNear;
        activeCamera.far = nfFar;
        camP.updateProjectionMatrix();
      }

      // Sync pane.camera to the camera actually used to render this pane.
      // _loopMultiView renders through the shared scene.cameraO / cameraP,
      // never through pane.camera — so without this, pane.camera keeps the
      // default projection it was constructed with. External consumers (the
      // alignment gizmo overlay) project world points into a pane via
      // `vec.project(pane.camera)` and read pane.camera.left/right/top/bottom
      // for pixel↔world ratios; both need to match what was rendered.
      {
        const srcCam = this.scene.getActiveCamera();
        const dstCam = pane.camera;
        if (dstCam) {
          dstCam.position.copy(srcCam.position);
          dstCam.quaternion.copy(srcCam.quaternion);
          dstCam.matrix.copy(srcCam.matrix);
          dstCam.matrixWorld.copy(srcCam.matrixWorld);
          dstCam.matrixWorldInverse.copy(srcCam.matrixWorldInverse);
          dstCam.projectionMatrix.copy(srcCam.projectionMatrix);
          if (dstCam.isOrthographicCamera && srcCam.isOrthographicCamera) {
            dstCam.left = srcCam.left;
            dstCam.right = srcCam.right;
            dstCam.top = srcCam.top;
            dstCam.bottom = srcCam.bottom;
            dstCam.near = srcCam.near;
            dstCam.far = srcCam.far;
          } else if (dstCam.isPerspectiveCamera && srcCam.isPerspectiveCamera) {
            dstCam.fov = srcCam.fov;
            dstCam.aspect = srcCam.aspect;
            dstCam.near = srcCam.near;
            dstCam.far = srcCam.far;
          }
        }
      }

      // Update material screenWidth/screenHeight to the pane size so the
      // shader-computed point sizes are correct for this viewport. The
      // updateMaterial call inside updatePointClouds set these to the full
      // canvas size, which makes points the wrong size in pane rendering.
      for (const pc of this.scene.pointclouds) {
        pc.material.screenWidth = vp.w;
        pc.material.screenHeight = vp.h;
      }

      // SCISSOR_TEST must be enabled per iteration: three.js's renderer.render
      // disables it at the end of its flow.
      this.renderer.setScissorTest(true);
      this.renderer.setViewport(vp.x, vp.y, vp.w, vp.h);
      this.renderer.setScissor(vp.x, vp.y, vp.w, vp.h);

      // Render using the high-level Potree renderer (same pipeline as
      // single-view, just driven from our pane viewport + camera state).
      // We pass `scissor` so PotreeRenderer.render re-applies the pane's
      // scissor rect before every internal renderer.render() / pRenderer
      // call — without it, point splats and overlay geometry bleed past the
      // pane boundary into adjacent panes (three.js's renderer.render() does
      // not preserve scissor across calls).
      pRenderer.clear();
      pRenderer.render({
        camera: this.scene.getActiveCamera(),
        viewport: [vp.x, vp.y, vp.w, vp.h],
        scissor: [vp.x, vp.y, vp.w, vp.h],
      });
    }

    this.renderer.setScissorTest(false);
    this.dispatchEvent({ type: 'render.pass.end' });
  }

  loop(timestamp) {
    // A frame already queued by setAnimationLoop can still fire after dispose().
    if (this._disposed) return;

    if (this.stats) {
      this.stats.begin();
    }

    if (Potree.measureTimings) {
      performance.mark('loop-start');
    }

    try {
      if (this.multiView) {
        this._loopMultiView(this.clock.getDelta(), timestamp);
      } else {
        this.update(this.clock.getDelta(), timestamp);
        this.render();
      }
      this._applyVisibleNodesFailsafe();
    } catch (e) {
      // Last-resort guard: setAnimationLoop schedules the next frame only after this
      // callback returns, so an uncaught exception here would permanently freeze the
      // viewer. Degrade gracefully by cutting the point budget instead of dying.
      console.error('[Potree] render loop error - degrading quality to stay alive:', e);
      const safe = Math.max(100000, Math.round(this.getPointBudget() * 0.5));
      this.setPointBudget(safe);
      this._loopErrorCount = (this._loopErrorCount || 0) + 1;
      if (this._loopErrorCount === 1) {
        try { this.postError('Wystąpił problem z renderowaniem - zmniejszono jakość, aby zachować stabilność.'); } catch (err) { /* noop */ }
      }
    }

    // let vrActive = viewer.renderer.xr.isPresenting;
    // if(vrActive){
    // 	this.update(this.clock.getDelta(), timestamp);
    // 	this.render();
    // }else{

    // 	this.update(this.clock.getDelta(), timestamp);
    // 	this.render();
    // }

    if (Potree.measureTimings) {
      performance.mark('loop-end');
      performance.measure('loop', 'loop-start', 'loop-end');
    }

    this.resolveTimings(timestamp);

    Potree.framenumber++;

    if (this.stats) {
      this.stats.end();
    }
  }

  postError(content, params = {}) {
    let message = this.postMessage(content, params);

    message.element.addClass('potree_message_error');

    return message;
  }

  postMessage(content, params = {}) {
    let message = new Message(content);

    let animationDuration = 100;

    message.element.hide();
    message.elClose.click(() => {
      message.element.slideUp(animationDuration, function () {
        $(this).remove();
      });

      let index = this.messages.indexOf(message);
      if (index >= 0) {
        this.messages.splice(index, 1);
      }
    });

    this.elMessages.prepend(message.element);

    // Use slideDown and then ensure flex display is restored
    message.element.slideDown(animationDuration, function () {
      $(this).css('display', 'flex');
    });

    this.messages.push(message);

    if (params.duration !== undefined) {
      let fadeDuration = 500;
      let slideOutDuration = 200;
      setTimeout(() => {
        message.element.animate(
          {
            opacity: 0,
          },
          fadeDuration
        );
        message.element.slideUp(slideOutDuration, function () {
          $(this).remove();
        });
      }, params.duration);
    }

    return message;
  }
}
